/**
 * Medical reference data — Interventions (drugs / fluids / procedures)
 *
 * Each intervention: metadata + a base `deltas` table, with optional
 * `modifierDeltas[modifierKey]` and `scenarioDeltas[scenarioKey]` overrides
 * that encode context-specific (linked) pharmacodynamic interactions.
 * Pure data — resolution logic lives in interventionEngine.js.
 */

// ─── Interventions ────────────────────────────────────────────────────────────

export const INTERVENTIONS = {
  ivFluid: {
    name: 'IV Fluid Bolus',
    shortName: 'IV Fluid',
    description: '↑BP  ↓HR slightly',
    onset: '~2 min',
    durationLabel: '~10 min',
    category: 'fluid',
    duration: 600_000,   // 10 min sim
    transitionMs: 120_000,  // 2 min ramp — slow volume redistribution
    // Dose presets — scale multiplies the deltas below (1 = reference dose).
    doses: [{ label: '250 mL', scale: 0.5 }, { label: '500 mL', scale: 1, default: true }, { label: '1 L', scale: 1.8 }],
    deltas: {
      HR:       -5,
      NIBP_Sys: +10,  NIBP_Dia: +6,
      ABP_Sys:  +10,  ABP_Dia:  +6,
      CVP:      +2,
    },
    modifierDeltas: {
      // Hypovolemia responds well to volume — augmented effect
      hypovolemia: {
        HR:       -8,
        NIBP_Sys: +18,  NIBP_Dia: +10,
        ABP_Sys:  +18,  ABP_Dia:  +10,
        CVP:      +3,
      },
    },
    scenarioDeltas: {
      // DKA: volume FIRST — restores perfusion, slows the compensatory tachypnea.
      dka: { HR: -18, NIBP_Sys: +18, NIBP_Dia: +10, ABP_Sys: +18, ABP_Dia: +10, RR: -4 },
      // Adrenal crisis: saline resuscitation is core therapy alongside hydrocortisone.
      adrenalCrisis: { HR: -10, NIBP_Sys: +12, NIBP_Dia: +7, ABP_Sys: +12, ABP_Dia: +7 },
      // RCA MI / RVI: FIRST-LINE treatment. The paralysed RV is entirely
      // preload-dependent — fluid passively pushes blood through. Rapid BP
      // improvement; CVP rises further as the tank fills. Stop if not responsive.
      rcaMI: {
        HR:       -3,    // 48 → 45 (minimal: already bradycardic, reduced compensatory drive)
        SpO2:     +4,    // 93 → 97 (improved CO → better O₂ delivery)
        NIBP_Sys: +22,   NIBP_Dia: +12,   // 82 → 104 / 52 → 64 (preload fills RV)
        ABP_Sys:  +22,   ABP_Dia:  +12,
        CVP:      +3,    // 14 → 17 (rising — target 8–12; titrate and stop)
        EtCO2:    +6,    // 28 → 34 (more CO₂ delivered to lungs with improved CO)
      },
      // Anaphylaxis: volume resuscitation partially reverses distributive shock.
      // Large bolus fills the vasodilated tank — improves MAP and reduces
      // compensatory tachycardia, but without epinephrine bronchospasm persists.
      anaphylaxis: {
        HR:       -25,   // 148 → 123 (partial — still tachycardic without epi)
        SpO2:     +3,    // 86 → 89  (slight improvement from improved cardiac output)
        NIBP_Sys: +32,   NIBP_Dia: +22,   // 58 → 90 / 30 → 52
        ABP_Sys:  +32,   ABP_Dia:  +22,
        CVP:      +4,    // 1 → 5 (venous return restored)
      },
      // Cardiac Tamponade: TEMPORISING only. A fluid bolus raises filling
      // pressure and passively supports preload against the compressing effusion —
      // a modest, transient BP bump that buys time — but CVP rises further and it
      // does NOT fix the obstruction. Pericardiocentesis is definitive.
      cardiacTamponade: {
        HR:       -6,    // 130 → 124 (slight — obstruction persists)
        NIBP_Sys: +12,   NIBP_Dia: +6,   // 84/68 → 96/74
        ABP_Sys:  +12,   ABP_Dia:  +6,
        EtCO2:    +3,    // 28 → 31
        CVP:      +2,    // 20 → 22 (tank fills further against the effusion)
      },
    },
  },

  bloodTransfusion: {
    name: 'Blood Transfusion',
    shortName: 'Transfusion',
    description: '↑↑BP  ↓↓HR',
    onset: '~5 min',
    durationLabel: '~20 min',
    category: 'fluid',
    duration: 1_200_000,  // 20 min sim
    transitionMs: 120_000,  // 2 min ramp
    deltas: {
      HR:       -10,
      NIBP_Sys: +18,  NIBP_Dia: +10,
      ABP_Sys:  +18,  ABP_Dia:  +10,
      CVP:      +3,
    },
    modifierDeltas: {
      // Greater benefit for hypovolemic patient (restores volume + O2 carrying capacity)
      hypovolemia: {
        HR:       -15,
        NIBP_Sys: +28,  NIBP_Dia: +15,
        ABP_Sys:  +28,  ABP_Dia:  +15,
        CVP:      +4,
      },
    },
    scenarioDeltas: {
      // RCA MI / RVI: blood volume expansion — greater preload effect than crystalloid.
      rcaMI: {
        HR:       -3,    // 48 → 45
        SpO2:     +5,    // 93 → 98
        NIBP_Sys: +26,   NIBP_Dia: +14,   // 82 → 108 / 52 → 66
        ABP_Sys:  +26,   ABP_Dia:  +14,
        CVP:      +4,    // 14 → 18
        EtCO2:    +7,    // 28 → 35
      },
      // Anaphylaxis: larger volume benefit than crystalloid; also restores O2 carrying
      // capacity and colloid osmotic pressure — greater BP and HR restoration.
      anaphylaxis: {
        HR:       -35,   // 148 → 113
        SpO2:     +4,    // 86 → 90
        NIBP_Sys: +42,   NIBP_Dia: +28,   // 58 → 100 / 30 → 58
        ABP_Sys:  +42,   ABP_Dia:  +28,
        CVP:      +5,    // 1 → 6
      },
    },
  },

  warmedIVFluid: {
    name: 'Warmed IV Fluids',
    shortName: 'Warmed IVF',
    description: '39–42 °C crystalloid — core rewarming  ↑Temp  ↑BP',
    onset: '5–10 min',
    durationLabel: '~20 min / L',
    category: 'fluid',
    duration: 1_200_000,   // 20 min sim
    transitionMs: 120_000,  // 2 min ramp
    // Without hypothermia: behaves like a standard fluid bolus with no meaningful
    // thermal effect (body thermoregulation easily handles warmed isotonic fluid).
    deltas: {
      HR:       -5,
      NIBP_Sys: +10,  NIBP_Dia: +5,
      ABP_Sys:  +10,  ABP_Dia:  +5,
      CVP:      +2,
    },
    scenarioDeltas: {
      // Hypothermia: primary therapeutic mechanism is core rewarming via the
      // vascular compartment.  Warm fluid raises core temperature, restores
      // SA-node automaticity, and partially reverses vasomotor collapse.
      // Partial correction only — combined with body warmer for full effect.
      hypothermia: {
        Temp:     +2.5,   // 26.0 → 28.5 °C (still hypothermic; approaching moderate)
        HR:       +10,    // 28 → 38  (SA node automaticity slowly returning)
        SpO2:     +15,    // 60 → 75  (peripheral perfusion improving, pleth more reliable)
        NIBP_Sys: +18,    NIBP_Dia: +10,   // 58 → 76 / 28 → 38
        ABP_Sys:  +18,    ABP_Dia:  +10,
        EtCO2:    +8,     // 16 → 24  (metabolism recovering → more CO₂ produced)
        RR:       +3,     // 3 → 6   (brainstem drive partially restored)
        CVP:      +2,
      },
    },
  },

  phenylephrine: {
    name: 'Phenylephrine',
    shortName: 'Phenyl.',
    description: 'α₁ agonist — ↑↑BP  ↓HR (reflex)',
    onset: 'Immediate–1 min',
    durationLabel: '5–15 min',
    category: 'vasopressor',
    duration: 600_000,   // 10 min sim (midpoint of 5–15 min)
    transitionMs: 45_000,
    // Dose presets — scale multiplies the deltas below (1 = reference dose).
    doses: [{ label: '50 µg', scale: 0.5 }, { label: '100 µg', scale: 1, default: true }, { label: '200 µg', scale: 1.6 }],
    deltas: {
      HR:       -8,
      NIBP_Sys: +22,  NIBP_Dia: +12,
      ABP_Sys:  +22,  ABP_Dia:  +12,
      CVP:      +1,
    },
    modifierDeltas: {
      // Hypovolemia: pure alpha-agonist cannot raise BP in an empty tank —
      // vasoconstriction with no preload gives minimal pressure response.
      hypovolemia: {
        HR:       -2,
        NIBP_Sys: +2,   NIBP_Dia: +1,
        ABP_Sys:  +2,   ABP_Dia:  +1,
        CVP:      0,
      },
    },
    scenarioDeltas: {
      // RCA MI / RVI: HARMFUL. Pure α₁ raises SVR (increases RV afterload/PVR)
      // while causing reflex bradycardia — catastrophic in a heart already at 48 bpm.
      // Minimal BP gain as the weakened RV cannot push against increased resistance.
      rcaMI: {
        HR:       -12,   // 48 → 36 (dangerous reflex bradycardia approaching arrest)
        SpO2:     -3,    // 93 → 90 (CO falls with slower rate and higher RV afterload)
        NIBP_Sys: +4,    NIBP_Dia: +2,    // 82 → 86 / 52 → 54 (minimal — RV can't use it)
        ABP_Sys:  +4,    ABP_Dia:  +2,
        CVP:      +3,    // 14 → 17 (more backup as RV fails against higher load)
        EtCO2:    -4,    // 28 → 24
      },
      // Hypotension: target scenario for phenylephrine (spinal/neuraxial hypotension).
      // Pure vasoconstriction raises SVR → MAP rises, reflex bradycardia included.
      hypotension: {
        HR:       -12,   // 120 → 108 (baroreflex bradycardia as MAP restores)
        NIBP_Sys: +48,   NIBP_Dia: +35,  // 70 → 118 / 40 → 75
        ABP_Sys:  +48,   ABP_Dia:  +35,
        SpO2:     +4,    // 94 → 98
        CVP:      +2,
      },
    },
  },

  ephedrine: {
    name: 'Ephedrine',
    shortName: 'Ephedrine',
    description: 'α/β mixed — ↑HR  ↑BP',
    onset: '1–2 min',
    durationLabel: '10–60 min',
    category: 'vasopressor',
    duration: 1_200_000,  // 20 min sim (shorter end; tachyphylaxis limits repeat dosing)
    transitionMs: 45_000,
    // Dose presets — scale multiplies the deltas below (1 = reference dose).
    doses: [{ label: '5 mg', scale: 0.6 }, { label: '10 mg', scale: 1, default: true }, { label: '20 mg', scale: 1.6 }],
    deltas: {
      HR:       +10,
      NIBP_Sys: +15,  NIBP_Dia: +8,
      ABP_Sys:  +15,  ABP_Dia:  +8,
      CVP:      +1,
    },
    modifierDeltas: {
      // Hypovolemia: some HR benefit (beta effect), BP partially blunted
      hypovolemia: {
        HR:       +8,
        NIBP_Sys: +8,   NIBP_Dia: +4,
        ABP_Sys:  +8,   ABP_Dia:  +4,
        CVP:      0,
      },
    },
    scenarioDeltas: {
      // Hypotension: mixed α/β effect — good first-line for bradycardia-associated
      // hypotension. Raises both HR and BP simultaneously.
      hypotension: {
        HR:       -15,   // 120 → 105 (slight slowing as MAP rises, but β1 maintained)
        NIBP_Sys: +42,   NIBP_Dia: +28,  // 70 → 112 / 40 → 68
        ABP_Sys:  +42,   ABP_Dia:  +28,
        SpO2:     +3,    // 94 → 97
        CVP:      +2,
      },
      // Bradycardia: β1 chronotropy augments rate while α1 partially restores MAP.
      bradycardia: {
        HR:       +28,   // 38 → 66 (modest; inferior to atropine for vagal bradycardia)
        NIBP_Sys: +20,   NIBP_Dia: +12,  // 95 → 115 / 60 → 72
        ABP_Sys:  +20,   ABP_Dia:  +12,
        CVP:      +1,
      },
    },
  },

  esmolol: {
    name: 'Esmolol',
    shortName: 'Esmolol',
    description: 'β₁ blocker — ↓↓HR  ↓BP',
    onset: '1–2 min',
    durationLabel: '10–20 min',
    category: 'rate-control',
    duration: 900_000,   // 15 min sim (midpoint of 10–20 min)
    transitionMs: 45_000,
    // Dose presets — scale multiplies the deltas below (1 = reference dose).
    doses: [{ label: '0.25 mg/kg', scale: 0.5 }, { label: '0.5 mg/kg', scale: 1, default: true }, { label: '1 mg/kg', scale: 1.6 }],
    deltas: {
      HR:       -25,
      NIBP_Sys: -8,   NIBP_Dia: -4,
      ABP_Sys:  -8,   ABP_Dia:  -4,
      CVP:      0,
    },
    // No modifier overrides — beta-blockade works independently of volume status
    scenarioDeltas: {
      // Serotonin syndrome: short-acting β-blockade for the autonomic surge.
      serotoninSyndrome: { HR: -32, NIBP_Sys: -16, NIBP_Dia: -9, ABP_Sys: -16, ABP_Dia: -9 },
      // Thyroid storm: β-blockade is first-line rate + adrenergic control.
      thyroidStorm: { HR: -45, NIBP_Sys: -14, NIBP_Dia: -8, ABP_Sys: -14, ABP_Dia: -8, Temp: -0.3 },
      // RCA MI / RVI: HARMFUL. β1 blockade further slows an ischaemic SA node
      // already at 48 bpm → critical bradycardia. Also drops BP in a patient
      // already in cardiogenic shock. Absolute clinical contraindication.
      rcaMI: {
        HR:       -16,   // 48 → 32 (life-threatening bradycardia)
        SpO2:     -4,    // 93 → 89
        NIBP_Sys: -16,   NIBP_Dia: -8,    // 82 → 66 / 52 → 44
        ABP_Sys:  -16,   ABP_Dia:  -8,
        CVP:      +2,    // 14 → 16 (worsening backup)
        EtCO2:    -5,    // 28 → 23
      },
      // Tachycardia: target scenario — β1 blockade aggressively slows rate.
      tachycardia: {
        HR:       -55,   // 140 → 85  (effective conversion to normal sinus)
        NIBP_Sys: -12,   NIBP_Dia: -6,   // 135 → 123 / 88 → 82
        ABP_Sys:  -12,   ABP_Dia:  -6,
        CVP:      0,
      },
      // VTach: esmolol can terminate haemodynamically stable VT.
      vtach: {
        HR:       -90,   // 180 → 90  (conversion to NSR)
        NIBP_Sys: +40,   NIBP_Dia: +24,  // 75 → 115 / 50 → 74 (CO restores with NSR)
        ABP_Sys:  +40,   ABP_Dia:  +24,
        SpO2:     +8,    // 85 → 93
        CVP:      -2,    // 8 → 6
      },
      // LAST: STRICTLY CONTRAINDICATED — β-blockade adds negative inotropy and
      // negative dromotropy to a myocardium already severely depressed by Na⁺-channel
      // blockade. The combined effect rapidly accelerates cardiovascular collapse → VF.
      // The heart has no contractile reserve to compensate for further rate/force reduction.
      //
      // Baseline: HR 160 (VT) / SpO₂ 72 / NIBP 52/28 / ABP 50/26 / EtCO₂ 8
      last: {
        rhythm:   'vf',
        HR:       -130,  // 160 → VF (negative dromotropy + Na⁺ block → conduction failure)
        SpO2:     -30,   // 72 → 42
        NIBP_Sys: -45,   NIBP_Dia: -25,   // 52 → ~7 / 28 → ~3
        ABP_Sys:  -45,   ABP_Dia:  -25,
        EtCO2:    -7,    // 8 → 1
      },
      // ST depression / demand ischaemia: rate control ↓ myocardial O₂ demand.
      stDepression: {
        HR:       -35,   // 95 → 60  (rate-related ischaemia driven by demand reduction)
        NIBP_Sys: -20,   NIBP_Dia: -10,  // 155 → 135 / 95 → 85
        ABP_Sys:  -20,   ABP_Dia:  -10,
        SpO2:     +2,    // 96 → 98
      },
    },
  },

  labetalol: {
    name: 'Labetalol',
    shortName: 'Labetalol',
    description: 'α₁/β blocker — ↓HR  ↓↓BP',
    onset: '2–5 min',
    durationLabel: '2–18 hr (sim: 30 min)',
    category: 'rate-control',
    duration: 1_800_000,  // 30 min sim (clinical: 2–18 hr, capped for practicality)
    transitionMs: 45_000,
    // Dose presets — scale multiplies the deltas below (1 = reference dose).
    doses: [{ label: '5 mg', scale: 0.5 }, { label: '10 mg', scale: 1, default: true }, { label: '20 mg', scale: 1.6 }],
    deltas: {
      HR:       -15,
      NIBP_Sys: -18,  NIBP_Dia: -10,
      ABP_Sys:  -18,  ABP_Dia:  -10,
      CVP:      0,
    },
    scenarioDeltas: {
      // Pheo crisis: predominantly β blockade (7:1) before α — unopposed
      // α-vasoconstriction, BP paradoxically rises. Discouraged agent.
      pheoCrisis: { HR: -12, NIBP_Sys: +15, NIBP_Dia: +8, ABP_Sys: +15, ABP_Dia: +8 },
      // RCA MI / RVI: HARMFUL. α/β blockade — β1 worsens bradycardia, α1 drops BP.
      // Double-hit in preload-dependent RV failure: slow rate + vasodilation.
      rcaMI: {
        HR:       -14,   // 48 → 34 (near-arrest bradycardia)
        SpO2:     -5,    // 93 → 88
        NIBP_Sys: -20,   NIBP_Dia: -10,   // 82 → 62 / 52 → 42
        ABP_Sys:  -20,   ABP_Dia:  -10,
        CVP:      +2,    // 14 → 16 (worsening congestion)
        EtCO2:    -6,    // 28 → 22
      },
      // Tachycardia: combined α/β effect — rate control plus BP modulation.
      tachycardia: {
        HR:       -40,   // 140 → 100
        NIBP_Sys: -22,   NIBP_Dia: -12,  // 135 → 113 / 88 → 76
        ABP_Sys:  -22,   ABP_Dia:  -12,
        CVP:      0,
      },
      // ST depression / hypertensive ischaemia: reduces both rate and afterload.
      stDepression: {
        HR:       -22,   // 95 → 73
        NIBP_Sys: -35,   NIBP_Dia: -18,  // 155 → 120 / 95 → 77
        ABP_Sys:  -35,   ABP_Dia:  -18,
        SpO2:     +2,
      },
      // LAST: STRICTLY CONTRAINDICATED — α/β blockade in a Na⁺-channel-poisoned heart.
      // α1 blockade vasodilates an already collapsed circulation; β1 blockade removes
      // what little contractile drive remains. The combination → rapid arrest.
      //
      // Baseline: HR 160 (VT) / SpO₂ 72 / NIBP 52/28 / ABP 50/26 / EtCO₂ 8
      last: {
        rhythm:   'vf',
        HR:       -125,  // 160 → VF
        SpO2:     -28,   // 72 → 44
        NIBP_Sys: -47,   NIBP_Dia: -25,   // vasodilation + ↓inotropy → near-zero MAP
        ABP_Sys:  -47,   ABP_Dia:  -25,
        EtCO2:    -7,    // 8 → 1
      },
    },
  },

  epinephrine: {
    name: 'Epinephrine',
    shortName: 'Epi',
    description: 'α/β potent — ↑↑HR  ↑↑BP',
    onset: 'Immediate',
    durationLabel: '1–5 min',
    category: 'vasopressor',
    duration: 300_000,   // 5 min sim
    transitionMs: 45_000,
    // Dose presets — scale multiplies the deltas below (1 = reference dose).
    doses: [{ label: '10 µg', scale: 0.3 }, { label: '100 µg', scale: 1, default: true }, { label: '1 mg', scale: 1.6 }],
    deltas: {
      HR:       +30,
      NIBP_Sys: +40,  NIBP_Dia: +20,
      ABP_Sys:  +40,  ABP_Dia:  +20,
      CVP:      +2,
    },
    modifierDeltas: {
      // Hypovolemia: chronotropy (HR) fully preserved (β1); vasopressor effect
      // (BP) significantly blunted because there is no preload to augment.
      hypovolemia: {
        HR:       +30,
        NIBP_Sys: +14,  NIBP_Dia: +7,
        ABP_Sys:  +14,  ABP_Dia:  +7,
        CVP:      +1,
      },
    },
    scenarioDeltas: {
      // Carcinoid crisis: catecholamines trigger further mediator release —
      // the hypotension paradoxically WORSENS. Use octreotide instead.
      carcinoidCrisis: { HR: +15, NIBP_Sys: -15, NIBP_Dia: -8, ABP_Sys: -15, ABP_Dia: -8 },
      // ── Anaphylaxis: first-line treatment. ────────────────────────────────
      // α1: massive vasoconstriction reverses distributive shock → ↑↑BP
      // β2: bronchodilation → EtCO₂ normalises, SpO₂ rises, RR slows
      // Removing the anaphylactic drive reduces compensatory tachycardia;
      // baroreflex from restored MAP also reflexively ↓HR — net HR falls.
      anaphylaxis: { vent: { vte: +160, ppeak: -28, pplat: -8 },
        HR:       -50,   // 148 → 98  (compensatory tachy resolves as BP normalises)
        SpO2:     +12,   // 86  → 98  (bronchodilation clears V/Q mismatch)
        NIBP_Sys: +58,   NIBP_Dia: +42,   // 58 → 116 / 30 → 72
        ABP_Sys:  +58,   ABP_Dia:  +42,
        EtCO2:    -20,   // 60 → 40  (bronchodilation → CO₂ no longer trapped)
        RR:       -12,   // 30 → 18  (work of breathing decreases)
        CVP:      +4,    // 1  → 5   (vasoconstriction restores venous return)
        Temp:     -0.3,  // 37.5 → 37.2
        // Bronchospasm resolved — capnogram normalises
        capnoMorphology: 'normal',
      },
      // ── Bronchospasm: β2-mediated bronchodilation ─────────────────────────
      // Epinephrine is rescue therapy when inhaled bronchodilators fail.
      // EtCO₂ drops as obstruction clears; SpO₂ and RR normalise.
      bronchospasm: { vent: { vte: +110, ppeak: -24, pplat: -6 },
        HR:       +5,    // 110 → 115 (mild β1 tachycardia — already tachycardic)
        SpO2:     +8,    // 91  → 99
        NIBP_Sys: +5,    NIBP_Dia: +3,   // already hypertensive; minimal rise
        ABP_Sys:  +5,    ABP_Dia:  +3,
        EtCO2:    -25,   // 65 → 40
        RR:       -8,    // 28 → 20
        capnoMorphology: 'normal',
      },
      // ── Bradycardia: β1 chronotropy ───────────────────────────────────────
      // Epinephrine is more potent than atropine for haemodynamically unstable
      // bradycardia. Full heart-rate rescue with BP recovery.
      bradycardia: {
        HR:       +40,   // 38 → 78
        NIBP_Sys: +28,   NIBP_Dia: +16,  // 95 → 123 / 60 → 76
        ABP_Sys:  +28,   ABP_Dia:  +16,
        CVP:      +1,
      },
      // ── RCA MI / RVI: last-resort only ────────────────────────────────────
      // β1 reverses SA/AV node ischaemic bradycardia; α1 restores coronary
      // perfusion pressure. BUT all catecholamines raise PVR — use only when
      // vasopressin + dobutamine + fluids have failed or in cardiac arrest.
      // Net effect: HR improves, MAP improves, but PVR rise slightly limits
      // SpO₂ gain and pushes CVP higher.
      rcaMI: {
        HR:       +22,   // 48 → 70 (β1 chronotropy rescues SA/AV node ischaemia)
        SpO2:     +3,    // 93 → 96 (improved CO, partly offset by ↑PVR)
        NIBP_Sys: +22,   NIBP_Dia: +12,   // 82 → 104 / 52 → 64
        ABP_Sys:  +22,   ABP_Dia:  +12,
        CVP:      +1,    // 14 → 15 (slight RV afterload increase)
        EtCO2:    +6,    // 28 → 34 (improved pulmonary blood flow)
      },
      // ── LAST: ASRA guideline — standard 1 mg ACLS dose HARMFUL ───────────
      // High-dose epinephrine exacerbates the arrhythmogenic state of the
      // Na⁺-channel-blocked myocardium: ↑automaticity + ↑O₂ demand in a heart
      // unable to repolarise normally → increased risk of intractable VF.
      // α1 vasopressor effect provides only a brief, partial BP response that is
      // quickly overwhelmed by worsening cardiac toxicity.
      // ASRA recommends ≤1 mcg/kg if absolutely necessary.
      //
      // Baseline: HR 160 (VT) / SpO₂ 72 / NIBP 52/28 / ABP 50/26 / EtCO₂ 8
      last: {
        HR:       +20,   // 160 → 180 (↑ myocardial excitability — VT worsens)
        SpO2:     -8,    // 72  → 64  (deteriorating cardiac output)
        NIBP_Sys: +4,    NIBP_Dia: +2,   // transient α1 effect, offset by ↓CO
        ABP_Sys:  +4,    ABP_Dia:  +2,
        EtCO2:    -3,    // 8 → 5 (less CO₂ delivered as CO falls)
      },
    },
  },

  norepinephrine: {
    name: 'Norepinephrine',
    shortName: 'Norepi',
    description: 'α₁>β₁ — ↑↑↑BP  ↓HR (reflex)',
    onset: 'Immediate',
    durationLabel: '1–2 min (offset)',
    category: 'vasopressor',
    duration: 300_000,   // 5 min sim for a bolus-equivalent effect
    transitionMs: 45_000,
    // Dose presets — scale multiplies the deltas below (1 = reference dose).
    doses: [{ label: '0.05 µg/kg/min', scale: 0.5 }, { label: '0.1 µg/kg/min', scale: 1, default: true }, { label: '0.3 µg/kg/min', scale: 1.6 }],
    deltas: {
      HR:       -8,
      NIBP_Sys: +35,  NIBP_Dia: +20,
      ABP_Sys:  +35,  ABP_Dia:  +20,
      CVP:      +2,
    },
    modifierDeltas: {
      // Hypovolemia: potent vasopressor effect partially preserved (better than
      // phenylephrine because norepinephrine also has some β1 to support CO).
      hypovolemia: {
        HR:       -4,
        NIBP_Sys: +18,  NIBP_Dia: +10,
        ABP_Sys:  +18,  ABP_Dia:  +10,
        CVP:      +1,
      },
    },
    scenarioDeltas: {
      // Carcinoid crisis: catecholamine pressors provoke mediator release — BP falls further.
      carcinoidCrisis: { HR: +10, NIBP_Sys: -12, NIBP_Dia: -6, ABP_Sys: -12, ABP_Dia: -6 },
      // RCA MI / RVI: useful to restore coronary perfusion pressure to the ischaemic
      // RV. α₁>β₁ raises MAP without the tachycardia of epinephrine. LITFL notes
      // caution — all catecholamines increase PVR more than vasopressin. Use only
      // if vasopressin is unavailable or BP remains critically low.
      rcaMI: {
        HR:       -3,    // 48 → 45 (mild reflex slowing — acceptable)
        SpO2:     +2,    // 93 → 95
        NIBP_Sys: +20,   NIBP_Dia: +12,   // 82 → 102 / 52 → 64
        ABP_Sys:  +20,   ABP_Dia:  +12,
        CVP:      +2,    // 14 → 16 (slight increase in venous return)
        EtCO2:    +4,    // 28 → 32
      },
      // Anaphylaxis: second-line vasopressor after epinephrine. Strong α1 restores
      // SVR; some β1 supports cardiac output. Does NOT address bronchospasm.
      anaphylaxis: {
        HR:       -28,   // 148 → 120 (baroreflex as BP rises, not full normalisation)
        SpO2:     +5,    // 86  → 91  (improved cardiac output, bronchospasm persists)
        NIBP_Sys: +54,   NIBP_Dia: +38,   // 58 → 112 / 30 → 68
        ABP_Sys:  +54,   ABP_Dia:  +38,
        CVP:      +3,
      },
    },
  },

  vasopressin: {
    name: 'Vasopressin',
    shortName: 'Vasopressin',
    description: 'V₁ agonist — ↑↑BP  HR neutral',
    onset: 'Immediate',
    durationLabel: '10–20 min',
    category: 'vasopressor',
    duration: 900_000,   // 15 min sim (midpoint of 10–20 min)
    transitionMs: 45_000,
    deltas: {
      HR:       +4,     // slight reflex increase possible
      NIBP_Sys: +28,  NIBP_Dia: +16,
      ABP_Sys:  +28,  ABP_Dia:  +16,
      CVP:      +1,
    },
    modifierDeltas: {
      // Hypovolemia: non-adrenergic mechanism — works better than catecholamines
      // in distributive/hemorrhagic shock because V1 receptors remain sensitive.
      hypovolemia: {
        HR:       +4,
        NIBP_Sys: +20,  NIBP_Dia: +11,
        ABP_Sys:  +20,  ABP_Dia:  +11,
        CVP:      +1,
      },
    },
    scenarioDeltas: {
      // RCA MI / RVI: LITFL-preferred vasopressor — V1 agonism raises MAP with
      // LESS effect on PVR than catecholamines, protecting the struggling RV.
      // Selective efferent arteriole constriction also supports renal perfusion.
      rcaMI: {
        HR:       0,     // vasopressin is HR-neutral (no β effect)
        SpO2:     +2,    // 93 → 95
        NIBP_Sys: +20,   NIBP_Dia: +11,   // 82 → 102 / 52 → 63
        ABP_Sys:  +20,   ABP_Dia:  +11,
        CVP:      +1,    // 14 → 15
        EtCO2:    +4,    // 28 → 32
      },
      // Anaphylaxis: adjunct vasopressor — V1 receptors are upregulated in shock
      // and respond well in catecholamine-refractory cases. Does not treat bronchospasm.
      anaphylaxis: {
        HR:       -15,   // 148 → 133 (baroreflex from restored BP)
        SpO2:     +3,    // 86  → 89  (improved O2 delivery; bronchospasm unchanged)
        NIBP_Sys: +42,   NIBP_Dia: +27,   // 58 → 100 / 30 → 57
        ABP_Sys:  +42,   ABP_Dia:  +27,
        CVP:      +3,
      },
      // LAST: NOT recommended — animal models demonstrate adverse outcomes including
      // pulmonary haemorrhage and no survival benefit when used to resuscitate a
      // local-anaesthetic-poisoned heart. The V1 mechanism does not address
      // Na⁺-channel blockade, and the vasoconstriction may worsen end-organ ischaemia.
      //
      // Baseline: HR 160 (VT) / SpO₂ 72 / NIBP 52/28 / ABP 50/26 / EtCO₂ 8
      last: {
        HR:       +8,    // 160 → 168 (arrhythmia worsens without inotropy)
        SpO2:     -10,   // 72  → 62  (no improvement; cardiac output deteriorates)
        NIBP_Sys: -5,    NIBP_Dia: -3,   // 52 → 47 / 28 → 25 — vasopressin cannot
        ABP_Sys:  -5,    ABP_Dia:  -3,   // rescue a Na⁺-blocked myocardium
        EtCO2:    -2,    // 8 → 6
      },
    },
  },

  dopamine: {
    name: 'Dopamine',
    shortName: 'Dopamine',
    description: 'D/β/α dose-dep — ↑HR  ↑↑BP',
    onset: 'Immediate',
    durationLabel: '<10 min (offset)',
    category: 'vasopressor',
    duration: 480_000,   // 8 min sim
    transitionMs: 45_000,
    deltas: {
      HR:       +18,
      NIBP_Sys: +22,  NIBP_Dia: +12,
      ABP_Sys:  +22,  ABP_Dia:  +12,
      CVP:      +1,
    },
    modifierDeltas: {
      // Hypovolemia: beta effects on HR preserved; vasopressor effect blunted
      hypovolemia: {
        HR:       +15,
        NIBP_Sys: +10,  NIBP_Dia: +5,
        ABP_Sys:  +10,  ABP_Dia:  +5,
        CVP:      0,
      },
    },
    scenarioDeltas: {
      // RCA MI / RVI: moderate-dose dopamine (β1 dominant) acts as inotrope
      // for weakened RV + mild vasopressor. Raises rate which helps CO.
      rcaMI: {
        HR:       +18,   // 48 → 66 (β1 chronotropy — critically improves CO)
        SpO2:     +3,    // 93 → 96
        NIBP_Sys: +16,   NIBP_Dia: +8,    // 82 → 98 / 52 → 60
        ABP_Sys:  +16,   ABP_Dia:  +8,
        CVP:      -1,    // 14 → 13 (slight forward flow improvement)
        EtCO2:    +6,    // 28 → 34
      },
      // Hypotension: moderate-dose dopamine (β1+α1) — raises both CO and SVR.
      hypotension: {
        HR:       -5,    // 120 → 115 (CO rise reduces compensatory tachy)
        NIBP_Sys: +48,   NIBP_Dia: +32,  // 70 → 118 / 40 → 72
        ABP_Sys:  +48,   ABP_Dia:  +32,
        SpO2:     +3,    // 94 → 97
        CVP:      +2,
      },
      // Bradycardia: low-moderate dose activates β1 → rate and contractility increase.
      bradycardia: {
        HR:       +35,   // 38 → 73
        NIBP_Sys: +22,   NIBP_Dia: +13,
        ABP_Sys:  +22,   ABP_Dia:  +13,
        CVP:      +1,
      },
    },
  },

  dobutamine: {
    name: 'Dobutamine',
    shortName: 'Dobutamine',
    description: 'β₁ inotrope — ↑CO  ↑HR  ↓SVR',
    onset: '1–2 min',
    durationLabel: '2–10 min',
    category: 'vasopressor',
    duration: 480_000,   // 8 min sim (midpoint of 2–10 min)
    transitionMs: 45_000,
    deltas: {
      HR:       +18,
      NIBP_Sys: +12,  NIBP_Dia: +4,    // CO up, SVR slightly down → modest MAP rise
      ABP_Sys:  +12,  ABP_Dia:  +4,
      CVP:      -1,                     // reduces filling pressures
    },
    modifierDeltas: {
      // Hypovolemia: inotrope needs preload — significantly blunted response
      hypovolemia: {
        HR:       +12,
        NIBP_Sys: +4,   NIBP_Dia: +1,
        ABP_Sys:  +4,   ABP_Dia:  +1,
        CVP:      0,
      },
    },
    scenarioDeltas: {
      // RCA MI / RVI: second-line after fluids. Non-β-adrenergic inotropy augments
      // whatever RV contractility remains. Also reduces PVR (mild PDE-V effect) —
      // the only inotrope that lowers RV afterload simultaneously. Ideal for RVI.
      rcaMI: {
        HR:       +14,   // 48 → 62 (β1 chronotropy helps rate and CO)
        SpO2:     +5,    // 93 → 98
        NIBP_Sys: +18,   NIBP_Dia: +8,    // 82 → 100 / 52 → 60
        ABP_Sys:  +18,   ABP_Dia:  +8,
        CVP:      -2,    // 14 → 12 (forward flow improves, reduces backup)
        EtCO2:    +7,    // 28 → 35
      },
      // STEMI / cardiogenic shock (stElevation): pump failure from ischaemia.
      // Dobutamine augments contractility without raising afterload → CO improves.
      stElevation: {
        HR:       +10,   // 88 → 98 (β1 tachycardia — monitor for demand ischaemia)
        NIBP_Sys: +22,   NIBP_Dia: +8,   // 100 → 122 / 65 → 73
        ABP_Sys:  +22,   ABP_Dia:  +8,
        SpO2:     +3,    // 94 → 97
        CVP:      -2,    // 7 → 5 (improved forward flow reduces back pressure)
        EtCO2:    +3,    // 34 → 37 (increased CO2 delivery to lungs)
      },
    },
  },

  // ── Antiarrhythmics ──────────────────────────────────────────────────────

  atropine: {
    name: 'Atropine',
    shortName: 'Atropine',
    description: 'Anticholinergic — ↑↑HR  ↑BP slightly',
    onset: '1–2 min',
    durationLabel: '15–30 min',
    category: 'antiarrhythmic',
    duration: 1_350_000,  // 22 min sim (midpoint of 15–30 min)
    transitionMs: 45_000,
    // Dose presets — scale multiplies the deltas below (1 = reference dose).
    doses: [{ label: '0.4 mg', scale: 0.6 }, { label: '0.6 mg', scale: 1, default: true }, { label: '1 mg', scale: 1.4 }],
    deltas: {
      HR:       +25,
      NIBP_Sys: +6,   NIBP_Dia: +3,
      ABP_Sys:  +6,   ABP_Dia:  +3,
      CVP:      0,
    },
    // Volume status does not affect vagolytic mechanism
    scenarioDeltas: {
      // RCA MI / RVI: RCA supplies SA node (~60%) and AV node (~90%). Ischaemia
      // causes vagally-mediated bradycardia — atropine is FIRST-LINE for this.
      // Rate increase dramatically improves CO in the preload-dependent RV.
      rcaMI: {
        HR:       +20,   // 48 → 68 (vagolysis restores SA automaticity)
        SpO2:     +4,    // 93 → 97 (CO improves with better rate)
        NIBP_Sys: +12,   NIBP_Dia: +7,    // 82 → 94 / 52 → 59
        ABP_Sys:  +12,   ABP_Dia:  +7,
        CVP:      -1,    // 14 → 13 (marginal forward flow improvement)
        EtCO2:    +5,    // 28 → 33
      },
      // Bradycardia: target scenario. Vagolysis is fully effective in vagally-mediated
      // bradycardia. Augmented HR restoration → secondary BP recovery.
      bradycardia: {
        HR:       +42,   // 38 → 80  (atropine fully blocks vagal tone → near-normal rate)
        NIBP_Sys: +26,   NIBP_Dia: +16,  // 95 → 121 / 60 → 76
        ABP_Sys:  +26,   ABP_Dia:  +16,
        CVP:      +1,
      },
    },
  },

  adenosine: {
    name: 'Adenosine',
    shortName: 'Adenosine',
    description: 'AV blocker — transient asystolic pause, then SVT resumes',
    onset: 'Seconds',
    durationLabel: '~10 sec pause',
    category: 'antiarrhythmic',
    // Rapid IV bolus: effect within seconds and gone within seconds. The short
    // `duration` is the length of the asystolic pause before the rhythm recurs;
    // the fast `transitionMs` makes the rate crash quickly to match the flat ECG.
    duration: 10_000,    // ~10 sec asystolic pause (sim)
    transitionMs: 2_000, // near-instant onset (rapid push + flush)
    deltas: {
      HR:       -35,
      NIBP_Sys: -8,   NIBP_Dia: -4,
      ABP_Sys:  -8,   ABP_Dia:  -4,
      CVP:      0,
    },
    // Effect is on AV nodal conduction; volume status irrelevant.
    //
    // SVT scenarios: adenosine blocks the AV node and produces a brief ASYSTOLIC
    // PAUSE (rhythm:'asystole', values crash toward zero), after which the
    // re-entrant SVT recurs — the auto-revert restores rhythm 'svt' and the
    // pre-drug vitals. Each delta set is the negative of that scenario's baseline
    // so the crash-then-restore is symmetric (see computeInterventionTargets'
    // adenosine special case, which floors at 0 rather than vital.min).
    scenarioDeltas: {
      rapidSVTHypotension: {
        rhythm: 'asystole',
        HR:       -190,                    // 190 → 0  (asystolic pause)
        NIBP_Sys: -72,  NIBP_Dia: -45,     // 82/50 → 10/5
        ABP_Sys:  -72,  ABP_Dia:  -44,     // 80/48 → 8/4
        EtCO2:    -18,                      // 30 → 12  (no pulmonary blood flow)
      },
      epicSVT: {
        rhythm: 'asystole',
        HR:       -190,                    // 190 → 0
        NIBP_Sys: -64,  NIBP_Dia: -42,     // 72/46 → 8/4
        ABP_Sys:  -64,  ABP_Dia:  -40,     // 70/44 → 6/4
        EtCO2:    -18,                      // 30 → 12
      },
      palsSvtStable: {
        rhythm: 'asystole',
        HR:       -235,                    // 235 → 0
        NIBP_Sys: -90,  NIBP_Dia: -60,     // 100/65 → 10/5
        ABP_Sys:  -90,  ABP_Dia:  -58,     // 98/63 → 8/5
        EtCO2:    -24,                      // 36 → 12
      },
      palsSvtUnstable: {
        rhythm: 'asystole',
        HR:       -235,                    // 235 → 0
        NIBP_Sys: -42,  NIBP_Dia: -28,     // 50/32 → 8/4
        ABP_Sys:  -42,  ABP_Dia:  -26,     // 48/30 → 6/4
        EtCO2:    -16,                      // 28 → 12
      },
      // Tachycardia (generic): adenosine transiently blocks the AV node,
      // terminating re-entrant SVT. After conversion, rate normalises and BP
      // recovers (no asystolic pause modelled for this legacy scenario).
      tachycardia: {
        HR:       -65,   // 140 → 75  (conversion to NSR after AV block)
        NIBP_Sys: -5,    NIBP_Dia: -3,   // transient slight BP dip during block
        ABP_Sys:  -5,    ABP_Dia:  -3,
        CVP:      0,
      },
    },
  },

  amiodarone: {
    name: 'Amiodarone',
    shortName: 'Amiodarone',
    description: 'Class III — ↓HR  ↓BP  antidysrhythmic',
    onset: '5–15 min',
    durationLabel: 'Hours–days (sim: 30 min)',
    category: 'antiarrhythmic',
    duration: 1_800_000,  // 30 min sim (actual half-life is 40+ days)
    transitionMs: 45_000,
    deltas: {
      HR:       -18,
      NIBP_Sys: -10,  NIBP_Dia: -5,
      ABP_Sys:  -10,  ABP_Dia:  -5,
      CVP:      0,
    },
    scenarioDeltas: {
      // VTach: first-line antiarrhythmic for haemodynamically stable VT.
      // Suppresses ectopy and slows conduction → conversion to NSR with BP recovery.
      vtach: {
        HR:       -85,   // 180 → 95  (rhythm conversion)
        NIBP_Sys: +38,   NIBP_Dia: +22,  // 75 → 113 / 50 → 72 (CO restores)
        ABP_Sys:  +38,   ABP_Dia:  +22,
        SpO2:     +7,    // 85 → 92
        CVP:      -1,
      },
      // VF/Cardiac arrest: post-shock antiarrhythmic in ACLS algorithm.
      // After defibrillation, amiodarone stabilises rhythm → ROSC.
      vf: {
        HR:       +60,   // 0 → 60   (ROSC — organised rhythm emerging)
        NIBP_Sys: +65,   NIBP_Dia: +35,  // 0 → 65 / 0 → 35
        ABP_Sys:  +65,   ABP_Dia:  +35,
        SpO2:     +48,   // 40 → 88  (ventilation/perfusion restored)
        EtCO2:    +24,   // 0 → 24   (metabolic CO2 still elevated post-arrest)
        RR:       +12,   // 0 → 12   (assuming ventilated)
        CVP:      +3,
      },
      // Tachycardia (stable): slows rate via multi-channel blockade.
      tachycardia: {
        HR:       -48,   // 140 → 92
        NIBP_Sys: -14,   NIBP_Dia: -7,   // mild BP reduction from vasodilation
        ABP_Sys:  -14,   ABP_Dia:  -7,
        CVP:      0,
      },
      // LAST: amiodarone is the PREFERRED antiarrhythmic if one is absolutely needed —
      // it does not rely on Na⁺-channel blockade (unlike lidocaine) and has multi-
      // channel properties (K⁺, Ca²⁺, β). However, it still adds myocardial depression
      // to an already globally depressed heart. Use with extreme caution; not a rescue.
      // Provides marginal partial rhythm suppression at the cost of further ↓ contractility.
      //
      // Baseline: HR 160 (VT) / SpO₂ 72 / NIBP 52/28 / ABP 50/26 / EtCO₂ 8
      last: {
        HR:       -15,   // 160 → 145 (partial VT suppression — incomplete)
        SpO2:     +2,    // 72  → 74  (minimal; CO barely changes)
        NIBP_Sys: -6,    NIBP_Dia: -3,   // 52 → 46 / 28 → 25 (negative inotropy
        ABP_Sys:  -6,    ABP_Dia:  -3,   // worsens cardiac output slightly)
      },
    },
  },

  lidocaine: {
    name: 'Lidocaine',
    shortName: 'Lidocaine',
    description: 'Class Ib — ↓HR slightly  antidysrhythmic',
    onset: '1–2 min',
    durationLabel: '10–20 min',
    category: 'antiarrhythmic',
    duration: 900_000,   // 15 min sim
    transitionMs: 45_000,
    deltas: {
      HR:       -10,
      NIBP_Sys: -6,   NIBP_Dia: -3,
      ABP_Sys:  -6,   ABP_Dia:  -3,
      CVP:      0,
    },
    scenarioDeltas: {
      // VTach: Class Ib — alternative to amiodarone for VT. Suppresses PVCs.
      vtach: {
        HR:       -70,   // 180 → 110 (partial conversion; less potent than amiodarone)
        NIBP_Sys: +25,   NIBP_Dia: +14,  // 75 → 100 / 50 → 64
        ABP_Sys:  +25,   ABP_Dia:  +14,
        SpO2:     +5,    // 85 → 90
        CVP:      -1,
      },
      // VF/arrest: ACLS alternative to amiodarone post-defibrillation.
      vf: {
        HR:       +55,   // 0 → 55
        NIBP_Sys: +55,   NIBP_Dia: +30,
        ABP_Sys:  +55,   ABP_Dia:  +30,
        SpO2:     +42,   // 40 → 82  (inferior ROSC compared to amiodarone)
        EtCO2:    +20,   // 0 → 20
        RR:       +12,
        CVP:      +3,
      },
      // LAST: LETHAL ERROR — lidocaine is itself a local anaesthetic (Class Ib Na⁺-channel
      // blocker). Administering it in LAST additively compounds the existing Na⁺-channel
      // blockade, driving the already-depressed conduction system into complete arrest.
      // This is not a dose-response — even small doses worsen the toxicity catastrophically.
      //
      // Baseline: HR 160 (VT) / SpO₂ 72 / NIBP 52/28 / ABP 50/26 / EtCO₂ 8
      last: {
        rhythm:   'vf',
        HR:       -130,  // 160 → VF (additive Na⁺ blockade → conduction failure → arrest)
        SpO2:     -32,   // 72  → 40
        NIBP_Sys: -45,   NIBP_Dia: -25,   // 52 → ~7 / 28 → ~3 — cardiac output collapses
        ABP_Sys:  -45,   ABP_Dia:  -25,
        EtCO2:    -8,    // 8 → 0
        CVP:      -1,    // 1 → 0
      },
    },
  },

  magnesium: {
    name: 'Magnesium Sulfate',
    shortName: 'Magnesium',
    description: 'Electrolyte — ↓HR  ↓BP  anti-TdP',
    onset: 'Immediate',
    durationLabel: '30 min',
    category: 'antiarrhythmic',
    duration: 1_800_000,  // 30 min sim
    deltas: {
      HR:       -12,
      NIBP_Sys: -10,  NIBP_Dia: -5,
      ABP_Sys:  -10,  ABP_Dia:  -5,
      CVP:      0,
    },
    scenarioDeltas: {
      // Eclamptic seizure: MgSO₄ is first-line — terminates the seizure, apnoea
      // resolves so SpO₂/RR recover; modest BP reduction. Baseline
      // HR 132 / SpO₂ 84 / BP 192/116 / EtCO₂ 52 / RR 4.
      obSeizure: {
        HR:       -24,   // 132 → 108
        SpO2:     +13,   // 84 → 97 (ventilation restored)
        RR:       +9,    // 4 → 13
        EtCO2:    -13,   // 52 → 39
        NIBP_Sys: -14,   NIBP_Dia: -8,
        ABP_Sys:  -14,   ABP_Dia:  -8,
      },
      // VF (Torsades de Pointes): magnesium is first-line for TdP — terminates VF
      // driven by QT prolongation. Restores rhythm and haemodynamic stability.
      vf: {
        HR:       +58,   // 0 → 58  (organised rhythm emerging — ROSC)
        NIBP_Sys: +58,   NIBP_Dia: +32,  // 0 → 58 / 0 → 32
        ABP_Sys:  +58,   ABP_Dia:  +32,
        SpO2:     +45,   // 40 → 85
        EtCO2:    +22,   // 0 → 22
        RR:       +12,
        CVP:      +2,
      },
      // TdP: IV Magnesium 2 g is the definitive first-line treatment.
      // Mechanism: Mg²⁺ stabilises the cardiac membrane by blocking voltage-gated
      // Ca²⁺ channels, shortening the action potential duration and suppressing the
      // early afterdepolarisations (EADs) that trigger TdP re-entry.
      // Works regardless of serum Mg²⁺ level.
      //
      // Deltas from TdP baseline (HR 220 / SpO₂ 80 / NIBP 68/42 / ABP 65/40):
      tdp: {
        rhythm:   'normal',  // TdP terminates → sinus rhythm restores
        HR:       -142,      // 220 → 78 (sinus rate; tachycardia resolves)
        SpO2:     +17,       // 80 → 97
        NIBP_Sys: +52,   NIBP_Dia: +30,   // 68 → 120 / 42 → 72
        ABP_Sys:  +53,   ABP_Dia:  +32,   // 65 → 118 / 40 → 72
        EtCO2:    +16,       // 22 → 38
        RR:       -8,        // 24 → 16
        CVP:      -2,        // 7 → 5
      },
    },
  },

  // ── Antihypertensives ─────────────────────────────────────────────────────

  nitroglycerin: {
    name: 'Nitroglycerin',
    shortName: 'NTG',
    description: 'Venodilator — ↓BP  ↑HR (reflex)  ↓CVP',
    onset: '1–2 min',
    durationLabel: '3–5 min',
    category: 'antihypertensive',
    duration: 240_000,   // 4 min sim
    transitionMs: 45_000,
    // Dose presets — scale multiplies the deltas below (1 = reference dose).
    doses: [{ label: '50 µg', scale: 0.5 }, { label: '100 µg', scale: 1, default: true }, { label: 'infusion', scale: 1.4 }],
    deltas: {
      HR:       +12,
      NIBP_Sys: -18,  NIBP_Dia: -10,
      ABP_Sys:  -18,  ABP_Dia:  -10,
      CVP:      -2,
    },
    modifierDeltas: {
      // Hypovolemia + nitroglycerin = profound hypotension (venodilation on empty tank)
      hypovolemia: {
        HR:       +20,
        NIBP_Sys: -30,  NIBP_Dia: -16,
        ABP_Sys:  -30,  ABP_Dia:  -16,
        CVP:      -3,
      },
    },
    scenarioDeltas: {
      // RCA MI / RVI: ABSOLUTE CONTRAINDICATION. Venodilator drops venous return →
      // the preload-dependent, paralysed RV instantly loses all forward flow.
      // BP collapses catastrophically. Do NOT give NTG in inferior/RV STEMI.
      rcaMI: {
        HR:       +28,   // 48 → 76 (severe reflex tachycardia as BP plummets)
        SpO2:     -10,   // 93 → 83 (CO collapses — organs desaturating)
        NIBP_Sys: -38,   NIBP_Dia: -22,   // 82 → 44 / 52 → 30 — profound shock
        ABP_Sys:  -38,   ABP_Dia:  -22,
        CVP:      -6,    // 14 → 8 (preload depleted by venodilation)
        EtCO2:    -14,   // 28 → 14 (near-zero CO delivery to pulmonary vasculature)
      },
      // STEMI: reduces preload/afterload → ↓ wall tension and O2 demand.
      // Improves SpO₂ (decreased pulmonary congestion), but BP drops significantly.
      stElevation: {
        HR:       +8,    // 88 → 96  (reflex response to BP drop)
        NIBP_Sys: -22,   NIBP_Dia: -12,  // 100 → 78 / 65 → 53 (careful: can over-drop)
        ABP_Sys:  -22,   ABP_Dia:  -12,
        SpO2:     +3,    // 94 → 97 (reduced pulmonary oedema)
        CVP:      -3,    // 7 → 4   (venodilation reduces RV filling)
        EtCO2:    +2,    // 34 → 36
      },
      // ST depression / demand ischaemia: reduces wall stress → pain/ischaemia relief.
      stDepression: {
        HR:       +8,    // 95 → 103 (reflex tachycardia — may worsen demand; monitor)
        NIBP_Sys: -28,   NIBP_Dia: -14,  // 155 → 127 / 95 → 81
        ABP_Sys:  -28,   ABP_Dia:  -14,
        SpO2:     +2,
        CVP:      -3,
      },
    },
  },

  // ── Electrolytes & Resuscitation ──────────────────────────────────────────

  sodiumBicarb: {
    name: 'Sodium Bicarbonate',
    shortName: 'NaHCO₃',
    description: 'Buffer — corrects acidosis  ↑BP slightly',
    onset: 'Immediate',
    durationLabel: '15–30 min',
    category: 'electrolyte',
    duration: 1_350_000,  // 22 min sim
    transitionMs: 45_000,
    deltas: {
      HR:       +5,
      NIBP_Sys: +6,   NIBP_Dia: +3,
      ABP_Sys:  +6,   ABP_Dia:  +3,
      CVP:      0,
    },
    // Volume status does not substantially alter buffering mechanism
    scenarioDeltas: {
      // Hyperkalaemic arrest: alkalinisation shifts K⁺ intracellularly, so the
      // sine wave narrows back to organised complexes — seen on the next rhythm
      // check. Still PEA: output returns with ACLS once the recovery gate opens,
      // so this is rhythm-only (no haemodynamic delta implying ROSC).
      hyperkalemicArrest: {
        rhythm: 'normal',
      },
    },
  },

  calciumChloride: {
    name: 'Calcium Chloride',
    shortName: 'CaCl₂',
    description: 'Electrolyte — ↑myocardial contractility  ↑BP',
    onset: 'Immediate',
    durationLabel: '15–30 min',
    category: 'electrolyte',
    duration: 1_350_000,  // 22 min sim
    transitionMs: 45_000,
    deltas: {
      HR:       +8,
      NIBP_Sys: +12,  NIBP_Dia: +6,
      ABP_Sys:  +12,  ABP_Dia:  +6,
      CVP:      0,
    },
    scenarioDeltas: {
      // LAST: NOT an effective treatment. The primary mechanism of LAST is voltage-gated
      // Na⁺-channel blockade — not Ca²⁺-channel blockade. Exogenous calcium does not
      // displace the local anaesthetic from Na⁺ channels or restore normal conduction.
      // While LA do have some mild Ca²⁺-channel effects, calcium does not meaningfully
      // reverse cardiovascular collapse and is not included in modern ASRA LAST algorithms.
      // The small residual effect modelled here reflects marginal inotropy from the
      // limited Ca²⁺-blocking component only — not a rescue.
      //
      // Baseline: HR 160 (VT) / SpO₂ 72 / NIBP 52/28 / ABP 50/26 / EtCO₂ 8
      last: {
        HR:       0,     // no chronotropy — Na⁺ channels still fully blocked
        NIBP_Sys: +2,    NIBP_Dia: +1,   // negligible; does not reverse collapse
        ABP_Sys:  +2,    ABP_Dia:  +1,
      },
    },
  },

  hyperventilation: {
    name: 'Hyperventilation',
    shortName: 'Hypervent.',
    description: 'Ventilator — ↑RR  ↓↓EtCO₂  ↓ICP (temporary)',
    onset: '1–2 min',
    durationLabel: '5–10 min (temporising only)',
    category: 'airway',
    duration: 600_000,   // 10 min sim — brief bridge to definitive therapy
    transitionMs: 60_000,  // 60s ramp — ventilator change takes time to affect EtCO₂
    // Default: hypocapnia causes mild cerebral vasoconstriction and reduces
    // venous return from elevated intrathoracic pressure.
    deltas: {
      RR:       +10,
      EtCO2:    -10,
      SpO2:     +2,
      NIBP_Sys: -5,   NIBP_Dia: -3,
      ABP_Sys:  -5,   ABP_Dia:  -3,
      CVP:      -2,
    },
    scenarioDeltas: {
      // Increased ICP: temporising bridge while osmotic therapy takes effect.
      // Hypocapnia (target EtCO₂ ~30 mmHg) causes cerebral vasoconstriction
      // → ↓ cerebral blood volume → ↓ ICP.
      // Critically: this is NOT definitive treatment.  Cushing's haemodynamics
      // only partially improve — BP and HR barely move because the underlying
      // mass effect is unchanged.  Use alongside mannitol or HTS, not instead.
      increasedICP: {
        RR:       +15,   // 5  → 20  (active hyperventilation)
        EtCO2:    -22,   // 54 → 32  (mild-moderate hypocapnia; avoid < 25)
        SpO2:     +5,    // 91 → 96  (improved alveolar ventilation)
        HR:       +6,    // 40 → 46  (modest: brainstem compression partially eased)
        NIBP_Sys: -18,   NIBP_Dia: -4,  // 188 → 170 / 62 → 58  (partial — Cushing persists)
        ABP_Sys:  -18,   ABP_Dia:  -4,
        CVP:      -1,    // 9  → 8   (↑ intrathoracic pressure reduces venous return)
      },
    },
  },

  mannitol: {
    name: 'Mannitol',
    shortName: 'Mannitol',
    description: 'Osmotic diuretic — ↓ICP  ↓cerebral edema',
    onset: '15–30 min',
    durationLabel: '3–6 hr (sim: 20 min)',
    category: 'electrolyte',
    duration: 1_200_000,  // 20 min sim
    transitionMs: 45_000,
    // Default effect when no specific scenario is active: mild osmotic
    // diuresis reduces preload slightly, negligible haemodynamic change.
    deltas: {
      HR:       0,
      NIBP_Sys: -5,   NIBP_Dia: -3,
      ABP_Sys:  -5,   ABP_Dia:  -3,
      CVP:      -1,   // osmotic diuresis reduces intravascular volume
    },
    scenarioDeltas: {
      // Increased ICP: target scenario.  Osmotic gradient draws water from
      // swollen brain tissue → ICP falls → Cushing reflex unwinds:
      //   • Systolic drops markedly as sympathetic surge abates
      //   • Bradycardia resolves as baroreceptors see lower MAP
      //   • Respiratory pattern normalises as brainstem compression eases
      //   • EtCO2 drops as ventilation improves
      //   • CVP falls from osmotic diuresis (unlike HTS)
      increasedICP: {
        HR:       +40,   // 40  → 80  (vagal bradycardia resolves)
        NIBP_Sys: -78,   NIBP_Dia: +10,  // 188 → 110 / 62 → 72  (pulse pressure narrows)
        ABP_Sys:  -78,   ABP_Dia:  +10,
        SpO2:     +7,    // 91  → 98
        EtCO2:    -16,   // 54  → 38  (regular ventilation restored)
        RR:       +9,    // 5   → 14
        Temp:     -0.4,  // 38.2 → 37.8
        CVP:      -3,    // 9   → 6   (osmotic diuresis — distinguishes from HTS)
      },
    },
  },

  dantrolene: {
    name: 'Dantrolene',
    shortName: 'Dantrolene',
    description: 'Ryanodine receptor blocker — definitive MH treatment. ↓↓EtCO₂  ↓↓HR  ↓↓Temp',
    onset: '5–10 min',
    durationLabel: '60–90 min',
    category: 'airway',
    duration: 5_400_000,   // 90 min
    transitionMs: 45_000,
    // Default (no MH modifier): mild non-specific muscle relaxation effect only.
    // No meaningful haemodynamic change without the hypermetabolic substrate.
    deltas: {
      HR:    -3,
      Temp:  -0.1,
    },
    scenarioDeltas: {
      // NMS: dantrolene relieves the lead-pipe rigidity and its heat production.
      nms: { HR: -22, Temp: -1.4, EtCO2: -10, RR: -5, NIBP_Sys: -8, NIBP_Dia: -4, ABP_Sys: -8, ABP_Dia: -4 },
    },
    modifierDeltas: {
      // Malignant Hyperthermia: dantrolene is DEFINITIVE treatment.
      // It blocks ryanodine receptors → halts uncontrolled SR Ca²⁺ release →
      // terminates the hypermetabolic cascade.
      // Reversal is dramatic but not instantaneous — partial correction within
      // the first dose; full normalisation requires repeated dosing + cooling.
      // These deltas provide ~80 % normalisation of the MH deltas:
      //   MH deltas: EtCO2 +28, HR +35, Temp +3.2, RR +10, NIBP +15/+8, SpO2 -5
      malignantHyperthermia: {
        EtCO2:    -24,   // 38+28=66 → 66-24=42  (near-normal)
        HR:       -30,   // 72+35=107 → 107-30=77 (near-normal)
        Temp:     -2.6,  // 36.8+3.2=40.0 → 40.0-2.6=37.4 (trending toward normal)
        RR:       -8,    // compensatory hyperventation resolves
        NIBP_Sys: -12,   NIBP_Dia: -6,
        ABP_Sys:  -12,   ABP_Dia:  -6,
        SpO2:     +4,    // 99-5=94 → 94+4=98 (near-normal)
        CVP:      -1,
      },
    },
  },

  hypertonicSaline: {
    name: '3% Hypertonic Saline',
    shortName: '3% HTS',
    description: 'Hyperosmolar — ↓ICP  ↑intravascular volume',
    onset: '10–20 min',
    durationLabel: '2–4 hr (sim: 15 min)',
    category: 'electrolyte',
    duration: 900_000,   // 15 min sim
    transitionMs: 45_000,
    // Default: volume expansion raises BP and CVP modestly.
    deltas: {
      HR:       -4,
      NIBP_Sys: +8,   NIBP_Dia: +5,
      ABP_Sys:  +8,   ABP_Dia:  +5,
      CVP:      +2,   // volume expansion — opposite of mannitol
    },
    scenarioDeltas: {
      // Increased ICP: same ICP-lowering mechanism as mannitol but also
      // expands intravascular volume — useful when patient is hypovolaemic.
      // Slightly faster acting; CVP rises (not falls) due to volume effect.
      increasedICP: {
        HR:       +38,   // 40  → 78
        NIBP_Sys: -72,   NIBP_Dia: +12,  // 188 → 116 / 62 → 74
        ABP_Sys:  -72,   ABP_Dia:  +12,
        SpO2:     +6,    // 91  → 97
        EtCO2:    -14,   // 54  → 40
        RR:       +8,    // 5   → 13
        Temp:     -0.3,
        CVP:      +2,    // 9   → 11  (volume expansion — key difference from mannitol)
      },
    },
  },

  venturiMask: {
    name: 'Venturi Mask',
    shortName: 'Venturi Mask',
    description: 'Supplemental O₂ (~24–60% FiO₂) — ↑SpO₂ in the non-intubated patient',
    onset: '30–60 s',
    durationLabel: 'Maintained (sim: 30 min)',
    category: 'airway',
    duration: 1_800_000,   // ongoing O₂ therapy; reverts if left the full session
    transitionMs: 30_000,  // SpO₂ climbs over ~½ min as FiO₂ washes in
    // Titratable moderate FiO₂. Does NOT touch ventSettings.fio2 (that's the
    // ventilator, for an intubated patient) — this raises the patient's SpO₂ directly.
    deltas: {
      SpO2: +5,
      HR:   -4,    // relief of hypoxic tachycardia
    },
    scenarioDeltas: {
      // V/Q mismatch / hypoventilation responds well to added FiO₂.
      hypoxia: { SpO2: +10, HR: -10 },   // 82 → 92
      // Dyshaemoglobinaemias: the pulse-ox reading does NOT reflect true
      // oxygenation and barely moves with FiO₂ (teaching point).
      methemoglobinemia: { SpO2: 0 },    // stuck ~85 — needs methylene blue
      carbonMonoxide:   { SpO2: 0 },     // reads falsely ~98 — give high-flow O₂ anyway (see NRB)
    },
  },

  nonRebreather: {
    name: 'Non-Rebreather Mask',
    shortName: 'Non-Rebreather',
    description: 'High-flow O₂ (~15 L/min, ~90% FiO₂) — maximal ↑SpO₂ pre-intubation',
    onset: '30–60 s',
    durationLabel: 'Maintained (sim: 30 min)',
    category: 'airway',
    duration: 1_800_000,
    transitionMs: 30_000,
    deltas: {
      SpO2: +10,
      HR:   -6,
    },
    scenarioDeltas: {
      hypoxia: { SpO2: +16, HR: -15 },   // 82 → 98 (near-full correction of V/Q mismatch)
      // Still unreliable in dyshaemoglobinaemia — SpO₂ is not measuring the problem.
      methemoglobinemia: { SpO2: +1 },   // ~86 despite high-flow O₂ — methylene blue is definitive
      carbonMonoxide:   { SpO2: 0 },     // DEFINITIVE Rx (↓COHb half-life) but pulse-ox reads ~98 unchanged
    },
  },

  intubation: {
    name: 'Endotracheal Intubation',
    shortName: 'Intubation',
    description:
      'Definitive airway — ETT placement enables reliable EtCO₂ capnography. ' +
      'EtCO₂ waveform appears on monitor after intubation if not already visible. ' +
      'Confirm placement: waveform capnography (primary) + bilateral breath sounds + chest rise.',
    onset: '30–60 s',
    durationLabel: 'Maintained',
    category: 'airway',
    duration: 7_200_000,   // 2 hr sim — represents maintained intubation
    transitionMs: 15_000,  // 15 s — tube placement + first few breaths
    //
    // channelEffects: applied once at intubation time.
    //   etco2: true — brings the EtCO₂ / capnography waveform row onto the monitor
    //                 if the instructor had it toggled off.
    // The prior visibility is captured at administration time and restored if the
    // intervention is cancelled (i.e. extubated).
    channelEffects: { etco2: true },
    //
    // Vital-sign deltas: intubation itself (tube placement, laryngoscopy stimulus)
    // has minor haemodynamic effects. Induction drugs (propofol, ketamine, sux, roc)
    // are separate interventions and carry the main pharmacodynamic burden.
    // Net: small transient sympathetic response from laryngoscopy stimulus.
    deltas: {
      HR:       +8,    // brief sympathetic stimulation from laryngoscopy
      NIBP_Sys: +10,   NIBP_Dia: +5,
      ABP_Sys:  +10,   ABP_Dia:  +5,
    },
    scenarioDeltas: {
      // Complete laryngospasm: once the tube passes the cords, the obstruction is
      // definitively relieved — EtCO₂ and SpO₂ recover as ventilation re-establishes.
      laryngospasmComplete: {
        capnoMorphology: 'normal',
        HR:    -20,
        SpO2:  +20,    // 70 → 90
        EtCO2: +35,    // 0  → 35  (first exhaled breaths through ETT)
        RR:    +12,    // 0  → 12  (ventilation re-started)
      },
      // Partial laryngospasm: intubation bypasses partial obstruction entirely.
      laryngospasmPartial: {
        capnoMorphology: 'normal',
        HR:    -10,
        SpO2:  +10,
        EtCO2: +15,
        RR:    +10,
      },

      // High / Total Spinal: intubation + positive-pressure ventilation with
      // 100% O₂ resolves the RESPIRATORY component (phrenic nerve apnoea).
      // SpO₂ and EtCO₂ normalise as controlled ventilation re-establishes gas exchange.
      //
      // The HAEMODYNAMIC component (sympathectomy → bradycardia + hypotension)
      // is NOT fixed by the airway — it persists and still requires ephedrine /
      // phenylephrine / atropine.  Vital delta reflects this correctly.
      //
      // Baseline: HR 38 / SpO₂ 62 / NIBP 48/28 / EtCO₂ 4 / RR 2
      obgynHighSpinal: {
        capnoMorphology: 'normal',
        SpO2:  +35,    // 62 → 97  (100% O₂ + PPV fully corrects apnoeic hypoxia)
        EtCO2: +33,    // 4  → 37  (gas exchange re-established via ETT)
        RR:    +10,    // 2  → 12  (controlled ventilation rate)
        HR:    +8,     // 38 → 46  (modest improvement from hypoxia relief; sympathectomy persists)
        // BP remains near-baseline — sympathectomy still present; treat with vasopressors
      },
    },
  },

  cricothyrotomy: {
    name: 'Cricothyrotomy (eFONA)',
    shortName: 'Cric',
    description:
      'Emergency front-of-neck access — the rescue for Cannot Intubate, Cannot ' +
      'Oxygenate (CICO). Scalpel–bougie–tube; confirm with EtCO₂ + breath sounds.',
    onset: '~1 min',
    durationLabel: 'Definitive',
    category: 'airway',
    duration: 7_200_000,   // 2 hr sim — surgical airway maintained
    transitionMs: 20_000,  // ~20 s to establish + first breaths
    // Bring the capnography row onto the monitor to confirm CO₂ return
    channelEffects: { etco2: true },
    deltas: {
      HR: +6,   // procedural sympathetic stimulus
    },
    scenarioDeltas: {
      // CICO difficult airway: front-of-neck access restores oxygenation and
      // ventilation. Baseline: SpO₂ 68 / EtCO₂ 20 / RR 0.
      epicDifficultAirway: {
        capnoMorphology: 'normal',
        SpO2:  +28,   // 68 → 96
        EtCO2: +18,   // 20 → 38 (gas exchange re-established)
        RR:    +12,   // 0  → 12
        HR:    -18,   // relief of hypoxic tachycardia
      },
      // Failed obstetric intubation → CICO rescue. Baseline: SpO₂ 78 / EtCO₂ 18 / RR 0.
      obFailedIntubation: {
        capnoMorphology: 'normal',
        SpO2:  +18,   // 78 → 96
        EtCO2: +20,   // 18 → 38
        RR:    +12,
        HR:    -20,
      },
      // Complete laryngospasm unrelieved from above — surgical airway bypasses it.
      laryngospasmComplete: {
        capnoMorphology: 'normal',
        SpO2:  +22,   // 70 → 92
        EtCO2: +35,   // 0  → 35
        RR:    +12,
        HR:    -18,
      },
    },
  },

  needleDecompression: {
    name: 'Needle Decomp. / Finger Thoracostomy',
    shortName: 'Needle Decomp.',
    description: 'Relieves tension PTX — immediate reversal of obstructive shock',
    onset: 'Immediate',
    durationLabel: '60 min (drain in situ)',
    category: 'airway',
    duration: 3_600_000,   // 60 min — represents ongoing chest drain / finger thoracostomy
    transitionMs: 45_000,
    // Default (no tension PTX): no meaningful effect — decompressing a normal
    // pleural space does not change haemodynamics.
    deltas: {
      HR: 0,
    },
    modifierDeltas: {
      // Tension Pneumothorax: DEFINITIVE treatment.
      //
      // Mechanism: needle (2nd ICS, MCL) or finger thoracostomy (4th/5th ICS,
      // anterior axillary line) vents trapped pleural air → intrathoracic pressure
      // falls → mediastinum returns to midline → SVC/IVC obstruction resolves →
      // cardiac output immediately restored.
      //
      // The capnoMorphology reverts from 'obstructed' to 'normal' because the
      // compressed airways re-expand once pleural pressure is relieved.
      //
      // Near-complete reversal of the tensionPneumothorax modifier deltas within
      // seconds to minutes:
      //   tensionPneumothorax deltas: EtCO2 −22, HR +38, NIBP −55/−30,
      //                               SpO2 −22, CVP +9, capnoMorphology: 'obstructed'
      tensionPneumothorax: { vent: { vte: +380, ppeak: -30, pplat: -28 },
        capnoMorphology: 'normal',   // airways re-expand → shark-fin resolves
        EtCO2:    +20,               // dead-space resolves as pulmonary perfusion returns
        HR:       -34,               // obstructive shock resolves, tachycardia abates
        NIBP_Sys: +52,  NIBP_Dia: +28,   // cardiac output restored
        ABP_Sys:  +52,  ABP_Dia:  +28,
        SpO2:     +20,               // collapsed lung re-expands → V/Q mismatch resolves
        CVP:      -8,                // venous obstruction released
      },
    },
  },

  thoracostomy: {
    name: 'Thoracostomy',
    shortName: 'Thoracostomy',
    description: 'Open/finger thoracostomy — decompresses tension pneumothorax and drains haemothorax; more reliable than a needle. Follow with a chest tube.',
    onset: 'Immediate', durationLabel: 'Bridge', category: 'procedure',
    duration: 3_600_000, transitionMs: 8_000,
    channelEffects: { etco2: true },
    deltas: { HR: -6, SpO2: +4, NIBP_Sys: +10, NIBP_Dia: +6, ABP_Sys: +10, ABP_Dia: +6, CVP: -3 },
    modifierDeltas: {
      tensionPneumothorax: { vent: { vte: +400, ppeak: -32, pplat: -29 }, capnoMorphology: 'normal', EtCO2: +20, HR: -34, NIBP_Sys: +52, NIBP_Dia: +28, ABP_Sys: +52, ABP_Dia: +28, SpO2: +20, CVP: -8 },
    },
    scenarioDeltas: {
      epicPneumothorax:  { capnoMorphology: 'normal', SpO2: +16, NIBP_Sys: +35, NIBP_Dia: +20, ABP_Sys: +35, ABP_Dia: +20, HR: -22, CVP: -7, EtCO2: +8 },
      palsTensionPneumo: { vent: { vte: +150, ppeak: -32, pplat: -30 }, SpO2: +24, NIBP_Sys: +30, NIBP_Dia: +18, ABP_Sys: +30, ABP_Dia: +18, HR: -24, CVP: -6 },
    },
  },

  chestTube: {
    name: 'Chest Tube Insertion',
    shortName: 'Chest Tube',
    description: 'Tube thoracostomy — definitive drainage of pneumothorax / haemothorax / large effusion; re-expands the lung.',
    onset: '~2 min', durationLabel: 'Definitive', category: 'procedure',
    duration: 7_200_000, transitionMs: 20_000,
    channelEffects: { etco2: true },
    deltas: { HR: -5, SpO2: +5, NIBP_Sys: +8, NIBP_Dia: +4, ABP_Sys: +8, ABP_Dia: +4, CVP: -2 },
    modifierDeltas: {
      tensionPneumothorax: { vent: { vte: +400, ppeak: -32, pplat: -29 }, capnoMorphology: 'normal', EtCO2: +18, HR: -30, NIBP_Sys: +48, NIBP_Dia: +26, ABP_Sys: +48, ABP_Dia: +26, SpO2: +18, CVP: -7 },
    },
    scenarioDeltas: {
      epicPneumothorax: { capnoMorphology: 'normal', SpO2: +16, NIBP_Sys: +32, NIBP_Dia: +18, ABP_Sys: +32, ABP_Dia: +18, HR: -20, CVP: -6, EtCO2: +8 },
    },
  },

  pelvicBinder: {
    name: 'Pelvic Binder',
    shortName: 'Pelvic Binder',
    description: 'Circumferential pelvic binder — reduces pelvic volume and tamponades venous bleeding in an unstable pelvic fracture. Temporising.',
    onset: 'Immediate', durationLabel: 'Temporising', category: 'procedure',
    duration: 3_600_000, transitionMs: 20_000,
    deltas: { HR: -8, NIBP_Sys: +10, NIBP_Dia: +6, ABP_Sys: +10, ABP_Dia: +6 },
    scenarioDeltas: {
      epicTrauma:     { HR: -15, NIBP_Sys: +18, NIBP_Dia: +10, ABP_Sys: +18, ABP_Dia: +10 },
      epicHemorrhage: { HR: -10, NIBP_Sys: +12, NIBP_Dia: +7, ABP_Sys: +12, ABP_Dia: +7 },
    },
  },

  irEmbolization: {
    name: 'IR Embolization',
    shortName: 'IR Embo',
    description: 'Interventional-radiology angioembolization — definitive control of arterial haemorrhage (pelvic, GI, uterine/obstetric).',
    onset: 'Delayed', durationLabel: 'Definitive', category: 'procedure',
    duration: 7_200_000, transitionMs: 45_000,
    deltas: { HR: -12, SpO2: +2, NIBP_Sys: +20, NIBP_Dia: +12, ABP_Sys: +20, ABP_Dia: +12 },
    scenarioDeltas: {
      epicHemorrhage:        { HR: -20, SpO2: +4, NIBP_Sys: +35, NIBP_Dia: +20, ABP_Sys: +35, ABP_Dia: +20 },
      obgynHemorrhagicShock: { HR: -20, SpO2: +4, NIBP_Sys: +35, NIBP_Dia: +20, ABP_Sys: +35, ABP_Dia: +20 },
      epicTrauma:            { HR: -15, NIBP_Sys: +28, NIBP_Dia: +16, ABP_Sys: +28, ABP_Dia: +16 },
    },
  },

  massiveTransfusion: {
    name: 'Massive Transfusion Protocol',
    shortName: 'MTP',
    description: 'Activate MTP — balanced 1:1:1 PRBC:FFP:platelets for massive haemorrhage; restores volume, oxygen carriage and clotting. Give calcium, keep warm.',
    onset: '~2 min', durationLabel: '~30 min', category: 'transfusion',
    duration: 1_800_000, transitionMs: 45_000,
    deltas: { HR: -18, SpO2: +4, NIBP_Sys: +28, NIBP_Dia: +16, ABP_Sys: +28, ABP_Dia: +16, CVP: +2 },
    scenarioDeltas: {
      epicHemorrhage:        { HR: -30, SpO2: +8, NIBP_Sys: +50, NIBP_Dia: +28, ABP_Sys: +50, ABP_Dia: +28, CVP: +4 },
      obgynHemorrhagicShock: { HR: -28, SpO2: +7, NIBP_Sys: +48, NIBP_Dia: +26, ABP_Sys: +48, ABP_Dia: +26, CVP: +4 },
      epicTrauma:            { HR: -25, SpO2: +6, NIBP_Sys: +42, NIBP_Dia: +24, ABP_Sys: +42, ABP_Dia: +24 },
    },
  },

  pericardiocentesis: {
    name: 'Pericardiocentesis',
    shortName: 'Pericardiocentesis',
    description: 'Drains pericardial effusion — immediate reversal of tamponade',
    onset: 'Immediate',
    durationLabel: '60 min (drain in situ)',
    category: 'equipment',
    duration: 3_600_000,   // 60 min — pericardial drain / catheter left in situ
    transitionMs: 20_000,  // haemodynamics recover rapidly as fluid is aspirated
    // Default (no effusion): draining an empty pericardium does nothing.
    deltas: {
      HR: 0,
    },
    scenarioDeltas: {
      // Cardiac Tamponade: DEFINITIVE treatment. Aspirating even 30–50 mL drops
      // intrapericardial pressure → ventricular filling is restored → obstructive
      // shock resolves. The ECG electrical alternans and arterial pulsus paradoxus
      // resolve (rhythm → normal), the tachycardia settles, pulse pressure widens,
      // and the equalized/elevated CVP falls. Near-complete reversal of the
      // cardiacTamponade derangement within seconds to minutes.
      cardiacTamponade: {
        rhythm: 'normal',            // alternans + pulsus resolve
        HR:       -45,               // 130 → 85 (obstructive shock relieved)
        NIBP_Sys: +40,  NIBP_Dia: +10,   // 84/68 → 124/78 (CO restored, PP widens)
        ABP_Sys:  +38,  ABP_Dia:  +10,   // 82/66 → 120/76
        EtCO2:    +12,               // 28 → 40 (pulmonary perfusion returns)
        SpO2:     +4,                // 94 → 98
        CVP:      -14,               // 20 → 6 (pericardial pressure released)
      },
    },
  },

  broadSpectrumAntibiotics: {
    name: 'Broad-Spectrum Antibiotics',
    shortName: 'Antibiotics',
    description: 'Empiric IV broad-spectrum antibiotics — source control for sepsis (give early)',
    onset: 'Source control (min–hr)',
    durationLabel: 'Maintained (sim)',
    category: 'other',
    duration: 3_600_000,    // ongoing therapy
    transitionMs: 300_000,  // slow ramp — antibiotics work over hours, not minutes
    // No acute haemodynamic effect in a non-septic patient (antibiotics don't
    // change vitals directly). The improvement below is scenario-specific.
    deltas: {
      HR: 0,
    },
    scenarioDeltas: {
      // Sepsis: early empiric antibiotics (Surviving Sepsis / Sepsis Six < 1 hr)
      // give gradual source control. PARTIAL improvement — the acute resuscitation
      // is fluids ± vasopressors; antibiotics reverse the driver over time.
      obMaternalSepsis: {
        HR:       -20,   // 130 → 110
        SpO2:     +2,
        NIBP_Sys: +18,   NIBP_Dia: +10,   // 82/44 → 100/54
        ABP_Sys:  +18,   ABP_Dia:  +10,
        EtCO2:    +6,    // 28 → 34 (perfusion improves, lactate clears)
        RR:       -6,    // 26 → 20
        Temp:     -1.5,  // 39.5 → 38.0 (fever defervesces)
        CVP:      +1,
      },
      palsSepticShock: {
        HR:       -40,   // 192 → 152
        SpO2:     +3,
        NIBP_Sys: +22,   NIBP_Dia: +12,   // 76/30 → 98/42
        ABP_Sys:  +22,   ABP_Dia:  +12,
        EtCO2:    +6,    // 30 → 36
        RR:       -12,   // 55 → 43
        Temp:     -1.2,  // 39.0 → 37.8
        CVP:      +2,
      },
    },
  },

  corticosteroid: {
    name: 'Dexamethasone / Hydrocortisone',
    shortName: 'Corticosteroid',
    description: 'Anti-inflammatory — ↓bronchospasm  ↓ICP (vasogenic)  ↓biphasic anaphylaxis',
    onset: '30–60 min',
    durationLabel: '4–8 hr (sim: 45 min)',
    category: 'other',
    duration: 2_700_000,   // 45 min sim
    transitionMs: 600_000,  // 10 min ramp — slow anti-inflammatory onset
    // Default: mild mineralocorticoid effect raises BP and HR slightly;
    // no clinically meaningful waveform changes in a healthy patient.
    deltas: {
      HR:       +4,
      NIBP_Sys: +5,  NIBP_Dia: +3,
      ABP_Sys:  +5,  ABP_Dia:  +3,
    },
    scenarioDeltas: {
      // Adrenal (Addisonian) crisis: hydrocortisone IS the treatment — restores
      // vascular catecholamine responsiveness; BP recovers over the transition.
      adrenalCrisis: { HR: -25, NIBP_Sys: +35, NIBP_Dia: +20, ABP_Sys: +35, ABP_Dia: +20, Temp: -0.5 },
      // Thyroid storm: blocks T4→T3 conversion + treats relative adrenal insufficiency.
      thyroidStorm: { HR: -10, Temp: -0.4 },
      // Myxedema coma: empiric hydrocortisone before T4 (possible coexisting adrenal failure).
      myxedemaComa: { NIBP_Sys: +6, NIBP_Dia: +4, ABP_Sys: +6, ABP_Dia: +4 },
      // Anaphylaxis: corticosteroids do not replace epinephrine but prevent the
      // biphasic reaction and accelerate resolution of residual bronchospasm and
      // urticaria.  Onset is slow (30–60 min) but the effect is meaningful.
      // capnoMorphology normalises as airway inflammation subsides.
      anaphylaxis: { vent: { vte: +40, ppeak: -8, pplat: -2 },
        capnoMorphology: 'normal',   // bronchospasm resolves with steroid
        HR:       -20,               // 148 → 128  (reduced histamine-driven tachycardia)
        SpO2:     +5,                // 86 → 91   (improved alveolar ventilation)
        NIBP_Sys: +14,  NIBP_Dia: +8,   // 58 → 72 / 30 → 38  (reduced vasodilation)
        ABP_Sys:  +14,  ABP_Dia:  +8,
        EtCO2:    -10,               // 60 → 50  (bronchospasm easing → better exhalation)
        RR:       -5,                // 30 → 25
        CVP:      +1,
      },
      // Bronchospasm: IV steroids are first-line adjunct to bronchodilators.
      // Reduce airway mucosal oedema and inflammatory mediator release.
      // capnoMorphology reverts as the obstructed pattern resolves.
      bronchospasm: { vent: { vte: +40, ppeak: -8, pplat: -2 },
        capnoMorphology: 'normal',   // shark-fin resolves as airway inflammation settles
        SpO2:     +4,                // 91 → 95
        EtCO2:    -12,               // 65 → 53
        HR:       -8,                // 110 → 102
        RR:       -6,                // 28 → 22
        NIBP_Sys: -8,  NIBP_Dia: -4,
        ABP_Sys:  -8,  ABP_Dia:  -4,
      },
      // Increased ICP: dexamethasone is indicated for vasogenic oedema (tumour,
      // abscess) — it reduces capillary permeability in the blood-brain barrier.
      // Note: NOT effective for cytotoxic oedema (ischaemia, trauma).
      // Partial Cushing's haemodynamic improvement as ICP falls.
      increasedICP: {
        HR:       +8,                // 40 → 48  (mild improvement in vagal tone)
        NIBP_Sys: -22,  NIBP_Dia: -5,   // 188 → 166 / 62 → 57  (ICP ↓ → Cushing reflex attenuates)
        ABP_Sys:  -22,  ABP_Dia:  -5,
        SpO2:     +2,                // 91 → 93
        EtCO2:    -5,                // 54 → 49
        RR:       +2,                // 5 → 7
      },
    },
  },

  insulinD50: {
    name: 'IV Insulin + D50W',
    shortName: 'Insulin + D50',
    description: 'Shifts K⁺ intracellularly — reverses hyperkalemia ECG changes',
    onset: '15–30 min',
    durationLabel: '2–4 hr (sim: 30 min)',
    category: 'other',
    duration: 1_800_000,   // 30 min sim
    transitionMs: 60_000,   // 60s ramp — intracellular K⁺ shift takes several minutes
    // Without hyperkalemia: minimal haemodynamic effect.  Insulin drives glucose
    // into cells and K⁺ follows; D50W prevents hypoglycaemia.  In a normokalaemic
    // patient the small K⁺ shift has no clinically detectable ECG or haemodynamic
    // consequence — captured here as negligible deltas.
    deltas: {
      HR: -2,
    },
    scenarioDeltas: {
      // Hyperkalemia: definitive pharmacological treatment.
      //
      // Mechanism: insulin activates Na⁺/K⁺-ATPase → drives K⁺ from extracellular
      // space into cells.  Serum [K⁺] falls ~0.6–1 mEq/L per dose.
      //
      // ECG: as K⁺ drops below ~6.5 mEq/L, P waves reappear, QRS narrows, and
      // the peaked T wave regresses — encoded as rhythm: 'normal' which swaps the
      // hyperkalemia Gaussian waveform back to the standard McSharry sinus pattern.
      //
      // Haemodynamics: partial reversal only within one sim window; full
      // normalisation would require repeat dosing (instructor can re-administer).
      hyperkalemia: {
        rhythm:   'normal',  // ECG reverts: P waves return, QRS narrows, T normalises
        HR:       +18,       // 48 → 66  (AV conduction restored as K⁺ falls)
        SpO2:     +3,        // 95 → 98
        NIBP_Sys: +16,  NIBP_Dia: +10,   // 86 → 102 / 52 → 62  (↑ contractility)
        ABP_Sys:  +16,  ABP_Dia:  +10,
        CVP:      -1,        // slight reduction as cardiac output improves
      },
      // Hyperkalaemic ARREST: shifting K⁺ narrows the sine wave back to organised
      // complexes — visible on the next rhythm check. The patient stays in PEA;
      // mechanical output returns only with ACLS once the recovery gate opens, so
      // this is deliberately rhythm-only (no haemodynamic delta implying ROSC).
      hyperkalemicArrest: {
        rhythm: 'normal',
      },
    },
  },

  intralipid: {
    name: 'Intralipid 20%',
    shortName: 'Intralipid 20%',
    description: 'Lipid sink antidote for LAST — pulls LA from cardiac tissue  ↑↑CO  ↑↑BP  restores rhythm',
    onset: '1–3 min',
    durationLabel: '~30 min',
    category: 'other',
    duration: 1_800_000,   // 30 min sim
    transitionMs: 30_000,  // rapid reversal — lipid sink effect is fast
    // Without LAST: fat emulsion has no meaningful cardiovascular effect in a
    // normodynamic patient.  Minimal deltas reflect the negligible haemodynamic
    // consequence of a single bolus dose in a normal circulation.
    deltas: {
      HR:       -2,
      NIBP_Sys: +2,  NIBP_Dia: +1,
      ABP_Sys:  +2,  ABP_Dia:  +1,
    },
    scenarioDeltas: {
      // LAST Phase 2 — lipid sink mechanism:
      //   The 20% lipid emulsion creates a separate aqueous/lipid partition in
      //   plasma.  Highly lipophilic local anesthetics (especially bupivacaine)
      //   redistribute from cardiac sodium channels into the lipid phase,
      //   restoring normal membrane excitability.  Also donates free fatty acids
      //   as direct energy substrate to the stunned myocardium.
      //
      //   Deltas computed from LAST scenario baseline targets:
      //   HR: 160 → 85 (VT terminates, sinus rhythm restores at compensatory rate)
      //   SpO2: 72 → 97 (perfusion + breathing restored)
      //   NIBP: 52/28 → 118/72
      //   ABP: 50/26 → 115/70
      //   EtCO2: 8 → 36  (cardiac output restored → CO₂ delivered to lungs; breathing resumes)
      //   RR: 0 → 14     (cerebral perfusion restored → brainstem respiratory drive returns)
      //   CVP: 1 → 4     (venous return normalises)
      last: {
        rhythm:   'normal',   // VT → sinus: Na⁺ channels unblocked as LA redistributes
        HR:       -75,        // 160 → 85  (VT terminated; sinus rate reflects residual stress)
        SpO2:     +25,        // 72  → 97
        NIBP_Sys: +66,  NIBP_Dia: +44,   // 52/28 → 118/72
        ABP_Sys:  +65,  ABP_Dia:  +44,   // 50/26 → 115/70
        EtCO2:    +28,        // 8   → 36
        RR:       +14,        // 0   → 14  (brainstem perfusion restored)
        CVP:      +3,         // 1   → 4
      },
    },
  },

  // ── Sedatives ─────────────────────────────────────────────────────────────

  propofol: {
    name: 'Propofol',
    shortName: 'Propofol',
    description: 'GABA_A potentiation — ↓↓BP  ↑HR (reflex)  ↓SpO₂',
    onset: '30–60 sec',
    durationLabel: '~10 min',
    category: 'sedative',
    duration: 600_000,
    transitionMs: 30_000,
    // Bolus induction dose (2–2.5 mg/kg): vasodilation + mild negative inotropy →
    // significant BP drop; reflex tachycardia partially compensates.
    // Dose presets — scale multiplies the deltas below (1 = reference dose).
    doses: [{ label: '0.5 mg/kg', scale: 0.4 }, { label: '2 mg/kg', scale: 1, default: true }, { label: '3 mg/kg', scale: 1.4 }],
    deltas: {
      HR:       +10,
      NIBP_Sys: -20,  NIBP_Dia: -12,
      ABP_Sys:  -20,  ABP_Dia:  -12,
      SpO2:     -3,   // respiratory depression; assumes airway managed
    },
    modifierDeltas: {
      // Hypovolemia: vasodilation on a volume-depleted tank → haemodynamic collapse.
      // Induction in haemorrhagic shock with propofol is a known cause of peri-induction arrest.
      hypovolemia: {
        HR:       +25,
        NIBP_Sys: -38,  NIBP_Dia: -22,
        ABP_Sys:  -38,  ABP_Dia:  -22,
        SpO2:     -8,
      },
    },
    scenarioDeltas: {
      // Hypotension: propofol vasodilation compounds an already compromised MAP.
      hypotension: {
        HR:       +18,
        NIBP_Sys: -30,  NIBP_Dia: -18,
        ABP_Sys:  -30,  ABP_Dia:  -18,
        SpO2:     -5,
      },
      // Partial laryngospasm: deepening anaesthesia relaxes the partially spasmed
      // glottis. Airway opens → SpO₂ recovers, EtCO₂ normalises, shark-fin capnogram
      // resolves. Trade-off: propofol vasodilation still causes significant hypotension.
      //
      // Baseline: HR 115 / SpO₂ 88 / NIBP 105/65 / EtCO₂ 28 / RR 24
      laryngospasmPartial: {
        capnoMorphology: 'normal',   // obstructed pattern resolves as airway opens
        SpO2:     +9,    // 88 → 97
        HR:       -30,   // 115 → 85 (hypoxic drive removed; propofol slows rate)
        NIBP_Sys: -17,   NIBP_Dia: -13,   // 105 → 88 / 65 → 52 (propofol vasodilation)
        ABP_Sys:  -17,   ABP_Dia:  -13,
        EtCO2:    +8,    // 28 → 36 (normal tidal ventilation resumes)
        RR:       -8,    // 24 → 16
      },
      // Complete laryngospasm: propofol CANNOT break complete glottic closure —
      // the maximal sustained contraction of the thyroarytenoid muscle requires NMJ
      // blockade, not deeper anaesthesia. Propofol worsens haemodynamics (vasodilation
      // on a hypoxic, catecholamine-driven circulation) without resolving the airway.
      //
      // Baseline: HR 135 / SpO₂ 70 / NIBP 95/55 / EtCO₂ 0 / RR 0
      laryngospasmComplete: {
        HR:       +10,   // 135 → 145 (hypoxia + vasodilation reflex — worsening)
        SpO2:     -5,    // 70 → 65 (airway not opened; hypoxia progresses)
        NIBP_Sys: -22,   NIBP_Dia: -12,   // 95 → 73 / 55 → 43 (vasodilation on empty circuit)
        ABP_Sys:  -22,   ABP_Dia:  -12,
      },
    },
  },

  ketamine: {
    name: 'Ketamine',
    shortName: 'Ketamine',
    description: 'NMDA antagonist — ↑↑HR  ↑↑BP  bronchodilator  dissociative',
    onset: '1–2 min',
    durationLabel: '~15 min',
    category: 'sedative',
    duration: 900_000,
    transitionMs: 45_000,
    // 1–2 mg/kg IV: indirect sympathomimetic — blocks catecholamine reuptake →
    // ↑HR and ↑BP; bronchodilator (preferred induction in bronchospasm and shock).
    // Dose presets — scale multiplies the deltas below (1 = reference dose).
    doses: [{ label: '0.5 mg/kg', scale: 0.4 }, { label: '1.5 mg/kg', scale: 1, default: true }, { label: '2 mg/kg', scale: 1.3 }],
    deltas: {
      HR:       +20,
      NIBP_Sys: +18,  NIBP_Dia: +10,
      ABP_Sys:  +18,  ABP_Dia:  +10,
    },
    modifierDeltas: {
      // Hypovolemia: catecholamine release still effective — preferred agent for
      // haemorrhagic or distributive shock induction. Less BP rise than normovolaemic
      // patient (depleted endogenous reserve), but still net positive.
      hypovolemia: {
        HR:       +12,
        NIBP_Sys: +22,  NIBP_Dia: +12,
        ABP_Sys:  +22,  ABP_Dia:  +12,
      },
    },
    scenarioDeltas: {
      // Hypotension: sympathomimetic response raises BP — ketamine is the induction
      // agent of choice in haemodynamically compromised patients.
      hypotension: {
        HR:       +18,
        NIBP_Sys: +28,  NIBP_Dia: +16,
        ABP_Sys:  +28,  ABP_Dia:  +16,
      },
      // Bronchospasm: direct bronchial smooth-muscle relaxation (β₂-like) reduces
      // airway resistance; EtCO₂ falls as air-trapping resolves, SpO₂ improves.
      bronchospasm: { vent: { vte: +50, ppeak: -10, pplat: -2 },
        HR:       +15,
        NIBP_Sys: +10,  NIBP_Dia: +6,
        ABP_Sys:  +10,  ABP_Dia:  +6,
        EtCO2:    -10,  // 65 → 55 (partial reversal — not as effective as β₂ agonists)
        SpO2:     +5,
      },
      // Laryngospasm: ketamine preserves airway reflexes and provides
      // bronchodilation while maintaining BP — preferred adjunct.
      laryngospasmComplete: {
        HR:       +15,
        NIBP_Sys: +14,  NIBP_Dia: +8,
        ABP_Sys:  +14,  ABP_Dia:  +8,
        SpO2:     +4,   // partial improvement
      },
    },
  },

  dexmedetomidine: {
    name: 'Dexmedetomidine',
    shortName: 'Dex',
    description: 'α₂-agonist — ↓HR  ↓BP  sedation without apnea',
    onset: '5–10 min',
    durationLabel: '~30 min',
    category: 'sedative',
    duration: 1_800_000,
    transitionMs: 120_000,  // slow loading infusion
    // Loading infusion (1 mcg/kg over 10 min): central α₂ stimulation → ↓sympathetic
    // outflow → ↓HR and ↓BP. Unique: provides sedation without respiratory depression.
    deltas: {
      HR:       -15,
      NIBP_Sys: -12,  NIBP_Dia: -6,
      ABP_Sys:  -12,  ABP_Dia:  -6,
    },
    scenarioDeltas: {
      // Bradycardia: α₂ agonism further suppresses SA node automaticity.
      // CONTRAINDICATED in bradycardia — risk of severe bradycardia or asystole.
      bradycardia: {
        HR:       -18,   // 38 → 20 — dangerous
        NIBP_Sys: -18,   NIBP_Dia: -10,
        ABP_Sys:  -18,   ABP_Dia:  -10,
      },
      // Tachycardia: useful for perioperative rate control without myocardial
      // depression — ↓sympathetic tone, does not cause respiratory depression.
      tachycardia: {
        HR:       -30,   // 130 → 100
        NIBP_Sys: -10,   NIBP_Dia: -5,
        ABP_Sys:  -10,   ABP_Dia:  -5,
      },
      // Hypertension with tachycardia (e.g. emergence agitation):
      // central sympatholysis provides dual HR + BP control.
      increasedICP: {
        HR:       -18,
        NIBP_Sys: -18,   NIBP_Dia: -10,
        ABP_Sys:  -18,   ABP_Dia:  -10,
      },
    },
  },

  // ── Inhalational agents ──────────────────────────────────────────────────
  // Each card turns the vaporizer (or N₂O) on at ~1 MAC on the Aisys screen and
  // applies the agent's haemodynamic signature. Cancelling = vaporizer off.
  sevoflurane: {
    name: 'Sevoflurane', shortName: 'Sevo',
    description: 'Volatile — ET 2 % (1 MAC). ↓BP (vasodilation)  HR neutral  bronchodilator  ↓BIS',
    onset: '2–3 min (wash-in)', durationLabel: 'Maintained (until turned off)',
    category: 'inhalational',
    duration: 7_200_000,   // 2 h sim — maintained until cancelled ("vaporizer off")
    transitionMs: 120_000, // alveolar wash-in
    // Sets the vaporizer / fresh-gas mix on the ventilator screen (absolute
    // settings, restored on wear-off) plus 1-MAC haemodynamics. NOTE: an override
    // block REPLACES the base deltas, so each one repeats the vent block.
    // Wear-off / cancel = vaporizer off (other vent settings restored to prior).
    ventOff: { agent: 'none', agentPct: 0 },
    deltas: { vent: { agent: 'sevo', agentPct: 2.0 }, HR: 0, NIBP_Sys: -16, NIBP_Dia: -8, ABP_Sys: -16, ABP_Dia: -8 },
    scenarioDeltas: {
      bronchospasm: { vent: { agent: 'sevo', agentPct: 2.0, vte: +60, ppeak: -12, pplat: -3 }, SpO2: +3, EtCO2: -6, NIBP_Sys: -14, NIBP_Dia: -7, ABP_Sys: -14, ABP_Dia: -7, capnoMorphology: 'normal' },
      epicBronchospasm: { vent: { agent: 'sevo', agentPct: 2.0, vte: +60, ppeak: -12, pplat: -3 }, SpO2: +3, EtCO2: -6, NIBP_Sys: -14, NIBP_Dia: -7, ABP_Sys: -14, ABP_Dia: -7, capnoMorphology: 'normal' },
      epicMalignantHyperthermia: { vent: { agent: 'sevo', agentPct: 2.0 }, EtCO2: +10, HR: +12, Temp: +0.6, NIBP_Sys: -16, NIBP_Dia: -8, ABP_Sys: -16, ABP_Dia: -8 },
    },
    modifierDeltas: {
      // Malignant hyperthermia: volatile agents are the primary MH TRIGGER — drives the crisis harder.
      malignantHyperthermia: { vent: { agent: 'sevo', agentPct: 2.0 }, EtCO2: +10, HR: +12, Temp: +0.6, NIBP_Sys: -16, NIBP_Dia: -8, ABP_Sys: -16, ABP_Dia: -8 },
    },
  },
  desflurane: {
    name: 'Desflurane', shortName: 'Des',
    description: 'Volatile — ET 6 % (1 MAC). ↓BP  ↑HR (sympathetic surge)  airway irritant — worsens bronchospasm  ↓BIS',
    onset: '1–2 min (fast wash-in)', durationLabel: 'Maintained (until turned off)',
    category: 'inhalational',
    duration: 7_200_000,   // 2 h sim — maintained until cancelled ("vaporizer off")
    transitionMs: 120_000, // alveolar wash-in
    // Sets the vaporizer / fresh-gas mix on the ventilator screen (absolute
    // settings, restored on wear-off) plus 1-MAC haemodynamics. NOTE: an override
    // block REPLACES the base deltas, so each one repeats the vent block.
    // Wear-off / cancel = vaporizer off (other vent settings restored to prior).
    ventOff: { agent: 'none', agentPct: 0 },
    deltas: { vent: { agent: 'des', agentPct: 6.0 }, HR: +12, NIBP_Sys: -14, NIBP_Dia: -7, ABP_Sys: -14, ABP_Dia: -7 },
    scenarioDeltas: {
      bronchospasm: { vent: { agent: 'des', agentPct: 6.0, vte: -30, ppeak: +5, pplat: +1 }, HR: +15, SpO2: -2, EtCO2: +4, NIBP_Sys: -10, NIBP_Dia: -5, ABP_Sys: -10, ABP_Dia: -5 },
      epicBronchospasm: { vent: { agent: 'des', agentPct: 6.0, vte: -30, ppeak: +5, pplat: +1 }, HR: +15, SpO2: -2, EtCO2: +4, NIBP_Sys: -10, NIBP_Dia: -5, ABP_Sys: -10, ABP_Dia: -5 },
      epicMalignantHyperthermia: { vent: { agent: 'des', agentPct: 6.0 }, EtCO2: +10, HR: +12, Temp: +0.6, NIBP_Sys: -16, NIBP_Dia: -8, ABP_Sys: -16, ABP_Dia: -8 },
    },
    modifierDeltas: {
      // Malignant hyperthermia: volatile agents are the primary MH TRIGGER — drives the crisis harder.
      malignantHyperthermia: { vent: { agent: 'des', agentPct: 6.0 }, EtCO2: +10, HR: +12, Temp: +0.6, NIBP_Sys: -16, NIBP_Dia: -8, ABP_Sys: -16, ABP_Dia: -8 },
    },
  },
  isoflurane: {
    name: 'Isoflurane', shortName: 'Iso',
    description: 'Volatile — ET 1.2 % (1 MAC). ↓BP (vasodilation)  ↑HR (mild)  bronchodilator  ↓BIS',
    onset: '3–5 min (slower wash-in)', durationLabel: 'Maintained (until turned off)',
    category: 'inhalational',
    duration: 7_200_000,   // 2 h sim — maintained until cancelled ("vaporizer off")
    transitionMs: 120_000, // alveolar wash-in
    // Sets the vaporizer / fresh-gas mix on the ventilator screen (absolute
    // settings, restored on wear-off) plus 1-MAC haemodynamics. NOTE: an override
    // block REPLACES the base deltas, so each one repeats the vent block.
    // Wear-off / cancel = vaporizer off (other vent settings restored to prior).
    ventOff: { agent: 'none', agentPct: 0 },
    deltas: { vent: { agent: 'iso', agentPct: 1.2 }, HR: +8, NIBP_Sys: -16, NIBP_Dia: -8, ABP_Sys: -16, ABP_Dia: -8 },
    scenarioDeltas: {
      bronchospasm: { vent: { agent: 'iso', agentPct: 1.2, vte: +40, ppeak: -8, pplat: -2 }, SpO2: +2, EtCO2: -4, NIBP_Sys: -14, NIBP_Dia: -7, ABP_Sys: -14, ABP_Dia: -7 },
      epicBronchospasm: { vent: { agent: 'iso', agentPct: 1.2, vte: +40, ppeak: -8, pplat: -2 }, SpO2: +2, EtCO2: -4, NIBP_Sys: -14, NIBP_Dia: -7, ABP_Sys: -14, ABP_Dia: -7 },
      epicMalignantHyperthermia: { vent: { agent: 'iso', agentPct: 1.2 }, EtCO2: +10, HR: +12, Temp: +0.6, NIBP_Sys: -16, NIBP_Dia: -8, ABP_Sys: -16, ABP_Dia: -8 },
    },
    modifierDeltas: {
      // Malignant hyperthermia: volatile agents are the primary MH TRIGGER — drives the crisis harder.
      malignantHyperthermia: { vent: { agent: 'iso', agentPct: 1.2 }, EtCO2: +10, HR: +12, Temp: +0.6, NIBP_Sys: -16, NIBP_Dia: -8, ABP_Sys: -16, ABP_Dia: -8 },
    },
  },
  nitrousOxide: {
    name: 'Nitrous Oxide 50 %', shortName: 'N₂O',
    description: 'N₂O 50 % in O₂ — adds ~0.5 MAC, FiO₂ falls to 50 %. Minimal haemodynamic effect  mild ↓BIS. Not an MH trigger',
    onset: '1–2 min', durationLabel: 'Maintained (until turned off)',
    category: 'inhalational',
    duration: 7_200_000,   // 2 h sim — maintained until cancelled ("vaporizer off")
    transitionMs: 120_000, // alveolar wash-in
    // Sets the vaporizer / fresh-gas mix on the ventilator screen (absolute
    // settings, restored on wear-off) plus 1-MAC haemodynamics. NOTE: an override
    // block REPLACES the base deltas, so each one repeats the vent block.
    // Wear-off / cancel = vaporizer off (other vent settings restored to prior).
    ventOff: { n2o: 0 },
    deltas: { vent: { n2o: 50, fio2: 50 }, HR: 0, NIBP_Sys: -3, NIBP_Dia: -2, ABP_Sys: -3, ABP_Dia: -2 },
  },

  // ── Paralytics ────────────────────────────────────────────────────────────

  succinylcholine: {
    name: 'Succinylcholine',
    shortName: 'Sux / SCh',
    description: 'Depolarising NMJ blocker — ↓HR  fasciculations  ⚠ MH trigger  ⚠ ↑K⁺',
    onset: '30–60 sec',
    durationLabel: '~8 min',
    category: 'paralytic',
    duration: 480_000,
    transitionMs: 15_000,
    // RSI dose (1.5 mg/kg): depolarising block → fasciculations → flaccid paralysis.
    // Muscarinic bradycardia from nicotinic receptor stimulation; slight BP rise
    // from fasciculation-driven sympathetic discharge.
    deltas: {
      HR:       -8,
      NIBP_Sys: +4,   NIBP_Dia: +2,
      ABP_Sys:  +4,   ABP_Dia:  +2,
    },
    modifierDeltas: {
      // MH susceptibility: succinylcholine is a primary pharmacological trigger —
      // the depolarisation cascade releases massive Ca²⁺ from the SR, initiating
      // the hypermetabolic crisis. EtCO₂ rockets first (earliest clinical sign).
      malignantHyperthermia: {
        EtCO2:    +35,   // hypermetabolic VO₂ surge — earliest sign
        HR:       +45,   // massive catecholamine + metabolic drive
        Temp:     +2.0,  // rapid core temperature rise
        RR:       +12,   // compensatory hyperventilation
        NIBP_Sys: +20,   NIBP_Dia: +12,
        ABP_Sys:  +20,   ABP_Dia:  +12,
        SpO2:     -8,
      },
    },
    scenarioDeltas: {
      // Hyperkalemia: succinylcholine releases K⁺ from depolarising skeletal
      // muscle. In a normokalaemic patient ↑~0.5 mEq/L — tolerated.
      // In hyperkalemia the additional K⁺ load can cross the arrhythmia threshold
      // immediately → VF / cardiac arrest. ABSOLUTE CONTRAINDICATION.
      //
      // Baseline hyperkalemia: HR 60 / NIBP 86/52 / SpO₂ 95 / EtCO₂ 40
      hyperkalemia: {
        rhythm:   'vf',
        HR:       -55,   // 60 → 5 (agonal → VF)
        SpO2:     -55,   // 95 → 40 (cardiac arrest → no perfusion)
        NIBP_Sys: -80,   NIBP_Dia: -45,   // 86 → 6 / 52 → 7 → effectively 0
        ABP_Sys:  -80,   ABP_Dia:  -45,
        EtCO2:    -38,   // 40 → 2 (no cardiac output)
        CVP:      -2,
      },
      // Raised ICP: fasciculations transiently increase intragastric and intracranial
      // pressure. In an already elevated ICP scenario, this worsens cerebral perfusion.
      increasedICP: {
        HR:       -6,
        NIBP_Sys: +8,   NIBP_Dia: +5,
        ABP_Sys:  +8,   ABP_Dia:  +5,
      },
      // Partial laryngospasm: small-dose suxamethonium (0.1–0.2 mg/kg) is the
      // standard treatment — relaxes spasmed glottic muscles in ~30 s. Airway opens,
      // SpO₂ recovers, capnogram normalises. Minimal haemodynamic perturbation at low dose.
      //
      // Baseline: HR 115 / SpO₂ 88 / NIBP 105/65 / EtCO₂ 28 / RR 24
      laryngospasmPartial: {
        capnoMorphology: 'normal',
        SpO2:     +9,    // 88 → 97
        HR:       -35,   // 115 → 80 (hypoxic drive resolved; mild muscarinic bradycardia)
        NIBP_Sys: +2,    NIBP_Dia: +1,   // minimal change — low dose
        ABP_Sys:  +2,    ABP_Dia:  +1,
        EtCO2:    +8,    // 28 → 36
        RR:       -8,    // 24 → 16
      },
      // Complete laryngospasm: full RSI dose (1.5 mg/kg) required. Glottis opens
      // in ~30 s → immediate ability to ventilate. SpO₂ recovering but started low (70%).
      //
      // Baseline: HR 135 / SpO₂ 70 / NIBP 95/55 / EtCO₂ 0 / RR 0
      laryngospasmComplete: {
        capnoMorphology: 'normal',
        SpO2:     +18,   // 70 → 88 (recovering; not yet normal — hypoxic debt)
        HR:       -25,   // 135 → 110 (hypoxia drive easing; still tachycardic)
        NIBP_Sys: +8,    NIBP_Dia: +5,   // 95 → 103 / 55 → 60 (CO improving)
        ABP_Sys:  +8,    ABP_Dia:  +5,
        EtCO2:    +32,   // 0 → 32 (ventilation resumes; CO₂ rising rapidly)
        RR:       +14,   // 0 → 14
      },
    },
  },

  rocuronium: {
    name: 'Rocuronium',
    shortName: 'Roc',
    description: 'Non-depolarising NMJ blocker — minimal CV effect. Reversed by sugammadex.',
    onset: '60–90 sec',
    durationLabel: '30–45 min',
    category: 'paralytic',
    duration: 2_400_000,  // 40 min sim
    transitionMs: 30_000,
    // RSI dose (1.2 mg/kg): competitive ACh antagonist at NMJ. Mild vagolytic →
    // slight ↑HR. No histamine release, no cardiovascular instability.
    // Reversed completely by sugammadex (encapsulation, not enzyme competition).
    deltas: {
      HR: +5,   // mild vagolytic
    },
    scenarioDeltas: {
      // Partial laryngospasm: rocuronium (RSI dose) breaks spasm in ~60 s.
      // Slightly slower than suxamethonium but avoids depolarisation side-effects.
      // Good option when MH susceptibility or hyperkalaemia contraindicates sux.
      //
      // Baseline: HR 115 / SpO₂ 88 / NIBP 105/65 / EtCO₂ 28 / RR 24
      laryngospasmPartial: {
        capnoMorphology: 'normal',
        SpO2:     +8,    // 88 → 96
        HR:       -32,   // 115 → 83
        NIBP_Sys: +2,    NIBP_Dia: +1,
        ABP_Sys:  +2,    ABP_Dia:  +1,
        EtCO2:    +8,    // 28 → 36
        RR:       -8,    // 24 → 16
      },
      // Complete laryngospasm: 1.2 mg/kg rocuronium — onset ~60 s.
      // Baseline: HR 135 / SpO₂ 70 / NIBP 95/55 / EtCO₂ 0 / RR 0
      laryngospasmComplete: {
        capnoMorphology: 'normal',
        SpO2:     +16,   // 70 → 86
        HR:       -28,   // 135 → 107
        NIBP_Sys: +6,    NIBP_Dia: +4,
        ABP_Sys:  +6,    ABP_Dia:  +4,
        EtCO2:    +30,   // 0 → 30
        RR:       +14,   // 0 → 14
      },
    },
  },

  cisatracurium: {
    name: 'Cisatracurium',
    shortName: 'Cis',
    description: 'Non-depolarising — Hofmann elimination  no histamine  safe in organ failure',
    onset: '2–3 min',
    durationLabel: '40–75 min',
    category: 'paralytic',
    duration: 3_600_000,  // 60 min sim
    transitionMs: 60_000,
    // 0.15–0.2 mg/kg: stereoisomer of atracurium with no histamine release.
    // Hofmann elimination (spontaneous degradation at physiological pH/temp) —
    // organ-independent clearance makes it the preferred NMBA in hepatic or renal failure.
    // Minimal cardiovascular effect; slightly slower onset than rocuronium.
    deltas: {
      HR: -2,   // trivial; no histamine, no vagolytic
    },
    scenarioDeltas: {
      // Partial laryngospasm: cisatracurium onset is 2–3 min — significantly slower
      // than sux or rocuronium. Acceptable for partial spasm where SpO₂ is not
      // in freefall; preferred in liver/renal failure where organ-independent
      // Hofmann elimination avoids drug accumulation.
      //
      // Baseline: HR 115 / SpO₂ 88 / NIBP 105/65 / EtCO₂ 28 / RR 24
      laryngospasmPartial: {
        capnoMorphology: 'normal',
        SpO2:     +6,    // 88 → 94 (slower recovery; SpO₂ dipped further before onset)
        HR:       -25,   // 115 → 90
        NIBP_Sys: +1,    NIBP_Dia: +1,
        ABP_Sys:  +1,    ABP_Dia:  +1,
        EtCO2:    +6,    // 28 → 34
        RR:       -6,    // 24 → 18
      },
      // Complete laryngospasm: cisatracurium (2–3 min onset) is NOT recommended
      // for complete laryngospasm — SpO₂ will continue dropping from 70% for the
      // entire 2 min before the drug takes effect. Recovery is incomplete.
      //
      // Baseline: HR 135 / SpO₂ 70 / NIBP 95/55 / EtCO₂ 0 / RR 0
      laryngospasmComplete: {
        capnoMorphology: 'normal',
        SpO2:     +10,   // 70 → 80 (extended hypoxia before onset; poor recovery)
        HR:       -20,   // 135 → 115 (still tachycardic — prolonged hypoxic debt)
        NIBP_Sys: +4,    NIBP_Dia: +2,
        ABP_Sys:  +4,    ABP_Dia:  +2,
        EtCO2:    +28,   // 0 → 28
        RR:       +12,   // 0 → 12
      },
    },
  },

  sugammadex: {
    name: 'Sugammadex',
    shortName: 'Sugammadex',
    description: 'Selective relaxant binding agent — immediate reversal of rocuronium/vecuronium NMB. ↑↑RR  ↑↑SpO₂',
    onset: '2–3 min',
    durationLabel: 'Sustained reversal',
    category: 'paralytic',
    duration: 3_600_000,  // 60 min sim — effect is sustained (drug is eliminated, not reversed)
    transitionMs: 120_000, // 2 min ramp — onset at maximum recommended dose (16 mg/kg)
    deltas: {
      RR:       +8,     // partial restoration of spontaneous ventilation
      SpO2:     +4,
      HR:       -6,     // mild slowing as respiratory effort takes over
    },
    scenarioDeltas: {
      // Full NMB reversal (e.g. after rocuronium RSI, end of case): rapid, complete
      // restoration of spontaneous ventilation and neuromuscular function.
      laryngospasmComplete: {
        RR:       +14,   // 0 → 14 (spontaneous ventilation restored)
        SpO2:     +22,   // 70 → 92 (oxygenation recovers with ventilation)
        HR:       -20,   // 135 → 115 (hypoxic drive resolves)
        EtCO2:    +30,   // 0 → 30  (CO₂ reappears with ventilation)
        capnoMorphology: 'normal',
      },
    },
  },

  bodyWarmer: {
    name: 'Body Warmer',
    shortName: 'Body Warmer',
    description: 'Forced-air warming blanket — surface rewarming  ↑↑Temp',
    onset: '15–20 min',
    durationLabel: '~60 min',
    category: 'equipment',
    duration: 3_600_000,   // 60 min sim
    transitionMs: 45_000,
    // Without hypothermia: minimal effect — normothermic patients thermoregulate
    // easily and body warming is quickly offset by normal heat dissipation.
    deltas: {
      Temp: +0.2,
    },
    scenarioDeltas: {
      // Hypothermia: forced-air conductive warming raises surface then core
      // temperature.  Slower than warmed IVF (surface → core gradient) but
      // sustained over a longer window and produces greater total Temp rise.
      // Combined with warmed IVF: 26.0 + 2.5 + 3.5 = 32.0 °C — out of the
      // VF-risk zone (< 28 °C) and approaching moderate hypothermia territory.
      hypothermia: {
        Temp:     +3.5,   // 26.0 → 29.5 °C (severe → approaching moderate range)
        HR:       +15,    // 28 → 43   (SA node warming; rhythm more stable)
        SpO2:     +20,    // 60 → 80   (peripheral vasodilation returns)
        NIBP_Sys: +22,    NIBP_Dia: +12,   // 58 → 80 / 28 → 40
        ABP_Sys:  +22,    ABP_Dia:  +12,
        EtCO2:    +12,    // 16 → 28  (metabolic recovery → ↑CO₂ production)
        RR:       +4,     // 3 → 7   (brainstem respiratory drive improving)
        CVP:      +1,
      },
    },
  },

  transcutaneousPacemaker: {
    name: 'Transcutaneous Pacemaker',
    shortName: 'TCP',
    description: 'External pacing — emergency rate support  ↑↑HR',
    onset: 'Immediate',
    durationLabel: '~30 min',
    category: 'equipment',
    duration: 1_800_000,   // 30 min sim
    transitionMs: 15_000,  // 15 sec ramp
    // Primary effect: establishes paced rhythm at a set rate, overriding native bradycardia.
    // Provides mechanical capture but does not restore intrinsic contractility.
    deltas: {
      rhythm:   'paced',  // pacing spike + wide LBBB-like QRS, discordant T
      HR:       +45,      // 30 → 75 (emergency pacing rate; patient may need adjustment)
      NIBP_Sys: +8,       NIBP_Dia: +4,   // modest haemodynamic improvement from rate alone
      ABP_Sys:  +8,       ABP_Dia:  +4,
    },
    scenarioDeltas: {
      // Bradycardia (e.g. sick sinus, AV block, medication-induced): TCP restores rate
      // and improves cardiac output acutely. Does NOT address underlying conduction disease
      // (still requires definitive pacing or medication reversal).
      bradycardia: {
        rhythm:   'paced', // paced QRS morphology during active pacing
        HR:       +50,    // 38 → 88 (restored rate via pacing)
        NIBP_Sys: +18,    NIBP_Dia: +10,   // 95 → 113 / 60 → 70 (improved CO from rate)
        ABP_Sys:  +18,    ABP_Dia:  +10,
        SpO2:     +3,     // 96 → 99 (better perfusion)
        CVP:      0,      // no direct effect on preload
      },
      // TdP: overdrive pacing at 100–120 bpm eliminates the long R-R pauses
      // that create the vulnerable window for EAD-triggered re-entry ("R-on-T").
      // Unlike demand pacing (which only prevents bradycardia), overdrive pacing
      // actively suppresses TdP by shortening the QT interval at a faster rate.
      // The computeInterventionTargets special case recognises 'tdp' and sets a
      // fixed overdrive rate of 105 bpm rather than the demand-pacing floor.
      //
      // Deltas from TdP baseline (HR 220 → 105 handled by overdrive special case):
      tdp: {
        rhythm:   'normal',  // paced rhythm overrides polymorphic VT
        // HR is NOT listed here — the TCP overdrive branch in
        // computeInterventionTargets sets HR = 105 directly for TdP.
        SpO2:     +8,        // 80 → 88 (improved CO; SpO₂ continues to rise)
        NIBP_Sys: +26,   NIBP_Dia: +18,   // 68 → 94 / 42 → 60
        ABP_Sys:  +26,   ABP_Dia:  +18,   // 65 → 91 / 40 → 58
        EtCO2:    +10,       // 22 → 32
        RR:       -4,        // 24 → 20
        CVP:      -1,        // 7 → 6
      },
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Additional emergency drugs & therapies
  // ═══════════════════════════════════════════════════════════════════════════

  // ── Rate control & antihypertensives ──
  hydralazine: {
    name: 'Hydralazine', shortName: 'Hydral',
    description: 'Arterial vasodilator — ↓BP  ↑HR (reflex)',
    onset: '5–15 min', durationLabel: '~1 hr',
    category: 'antihypertensive', duration: 900_000, transitionMs: 90_000,
    // Dose presets — scale multiplies the deltas below (1 = reference dose).
    doses: [{ label: '5 mg', scale: 0.6 }, { label: '10 mg', scale: 1, default: true }, { label: '20 mg', scale: 1.5 }],
    deltas: { HR: +8, NIBP_Sys: -24, NIBP_Dia: -14, ABP_Sys: -24, ABP_Dia: -14 },
    scenarioDeltas: {
      obgynEclampsia:   { HR: +6, NIBP_Sys: -34, NIBP_Dia: -22, ABP_Sys: -34, ABP_Dia: -22 },
      obHELLP:          { HR: +6, NIBP_Sys: -30, NIBP_Dia: -18, ABP_Sys: -30, ABP_Dia: -18 },
      epicHypertension: { HR: +8, NIBP_Sys: -34, NIBP_Dia: -20, ABP_Sys: -34, ABP_Dia: -20 },
    },
  },

  nitroprusside: {
    name: 'Nitroprusside', shortName: 'SNP',
    description: 'Potent arterial + venous dilator — ↓↓BP, fast on/off',
    onset: '<1 min', durationLabel: '1–3 min',
    category: 'antihypertensive', duration: 180_000, transitionMs: 20_000,
    deltas: { HR: +6, NIBP_Sys: -40, NIBP_Dia: -22, ABP_Sys: -40, ABP_Dia: -22, CVP: -2 },
    scenarioDeltas: {
      epicHypertension: { HR: +6, NIBP_Sys: -48, NIBP_Dia: -28, ABP_Sys: -48, ABP_Dia: -28 },
      increasedICP:     { HR: +8, NIBP_Sys: -46, NIBP_Dia: -26, ABP_Sys: -46, ABP_Dia: -26 }, // lowers CPP — caution
    },
  },

  metoprolol: {
    name: 'Metoprolol', shortName: 'Metop',
    description: 'β-blocker — ↓HR (rate control)  ↓BP',
    onset: '2–5 min', durationLabel: '2–4 hr',
    category: 'rate-control', duration: 900_000, transitionMs: 60_000,
    deltas: { HR: -22, NIBP_Sys: -10, NIBP_Dia: -6, ABP_Sys: -10, ABP_Dia: -6 },
    scenarioDeltas: {
      // Rate control of AFib RVR restores diastolic filling → CO/BP improve
      rapidAfibHypotension: { HR: -40, NIBP_Sys: +6, NIBP_Dia: +4, ABP_Sys: +6, ABP_Dia: +4 },
      tachycardia:          { HR: -30, NIBP_Sys: -6, NIBP_Dia: -4, ABP_Sys: -6, ABP_Dia: -4 },
      // Pheo crisis: β-blockade BEFORE α-blockade → unopposed α-vasoconstriction —
      // the BP paradoxically RISES while the rate falls. Classic contraindication.
      pheoCrisis: { HR: -18, NIBP_Sys: +25, NIBP_Dia: +14, ABP_Sys: +25, ABP_Dia: +14 },
    },
  },

  diltiazem: {
    name: 'Diltiazem', shortName: 'Dilt',
    description: 'Ca-channel blocker — ↓HR (rate control)',
    onset: '2–5 min', durationLabel: '1–3 hr',
    category: 'rate-control', duration: 900_000, transitionMs: 60_000,
    deltas: { HR: -24, NIBP_Sys: -12, NIBP_Dia: -7, ABP_Sys: -12, ABP_Dia: -7 },
    scenarioDeltas: {
      rapidAfibHypotension: { HR: -42, NIBP_Sys: +4, NIBP_Dia: +3, ABP_Sys: +4, ABP_Dia: +3 },
    },
  },

  verapamil: {
    name: 'Verapamil', shortName: 'Verap',
    description: 'Ca-channel blocker (class IV) — ↓HR, more negative inotropy than diltiazem',
    onset: '2–5 min', durationLabel: '30 min–1 hr',
    category: 'rate-control', duration: 900_000, transitionMs: 60_000,
    deltas: { HR: -22, NIBP_Sys: -14, NIBP_Dia: -8, ABP_Sys: -14, ABP_Dia: -8 },
    scenarioDeltas: {
      rapidAfibHypotension: { HR: -40, NIBP_Sys: +2, NIBP_Dia: +2, ABP_Sys: +2, ABP_Dia: +2 },
      rapidSVTHypotension:  { HR: -55, NIBP_Sys: +8, NIBP_Dia: +5, ABP_Sys: +8, ABP_Dia: +5, rhythm: 'normal' },
    },
  },

  phentolamine: {
    name: 'Phentolamine', shortName: 'Phentol',
    description: 'α-blocker — first-line for catecholamine crisis  ↓↓BP, mild reflex ↑HR',
    onset: '1–2 min', durationLabel: '15–30 min',
    category: 'antihypertensive', duration: 1_200_000, transitionMs: 45_000,
    deltas: {
      NIBP_Sys: -30, NIBP_Dia: -16, ABP_Sys: -30, ABP_Dia: -16,
      HR: +8,    // reflex tachycardia from α-blockade
    },
    scenarioDeltas: {
      // Pheochromocytoma crisis: α-blockade directly counters the catecholamine
      // surge — dramatic BP response, and the reflex tachycardia is blunted
      // because the surge itself was driving the rate.
      pheoCrisis: { NIBP_Sys: -75, NIBP_Dia: -45, ABP_Sys: -75, ABP_Dia: -45, HR: -12 },
    },
  },

  insulinInfusion: {
    name: 'Insulin Infusion', shortName: 'Insulin gtt',
    description: 'IV insulin — closes the ketogenic gap in DKA (with fluids); watch K⁺',
    onset: '15–30 min', durationLabel: 'Continuous',
    category: 'endocrine', duration: 3_600_000, transitionMs: 120_000,
    deltas: { HR: -5 },
    scenarioDeltas: {
      // DKA: with volume, acidosis clears — Kussmaul respiration settles, EtCO₂
      // rises back toward normal as the compensatory hyperventilation stops.
      dka: { HR: -20, RR: -10, EtCO2: +12, NIBP_Sys: +8, NIBP_Dia: +5, ABP_Sys: +8, ABP_Dia: +5 },
    },
  },

  dextrose50: {
    name: 'Dextrose 50%', shortName: 'D50',
    description: 'IV glucose bolus — reverses hypoglycemia within minutes',
    onset: '1–3 min', durationLabel: '30–60 min',
    category: 'endocrine', duration: 1_800_000, transitionMs: 30_000,
    deltas: {},
    scenarioDeltas: {
      // Hypoglycemia: sympathetic surge (tachycardia, hypertension, sweating)
      // resolves as glucose normalises.
      hypoglycemia: { HR: -30, NIBP_Sys: -20, NIBP_Dia: -10, ABP_Sys: -20, ABP_Dia: -10, RR: -4 },
    },
  },

  levothyroxine: {
    name: 'IV Levothyroxine (T4)', shortName: 'T4',
    description: 'Thyroid replacement for myxedema coma — slow onset; give with hydrocortisone',
    onset: '6–12 hr (sim: min)', durationLabel: 'Continuous',
    category: 'endocrine', duration: 3_600_000, transitionMs: 180_000,
    deltas: {},
    scenarioDeltas: {
      // Myxedema coma: gradual chronotropic + thermogenic recovery. Modest in-sim
      // effect — supportive care (warming, ventilation, steroids) carries the case.
      myxedemaComa: { HR: +12, Temp: +0.8, RR: +3, NIBP_Sys: +8, NIBP_Dia: +5, ABP_Sys: +8, ABP_Dia: +5 },
    },
  },

  octreotide: {
    name: 'Octreotide', shortName: 'Octreo',
    description: 'Somatostatin analogue — first-line for carcinoid crisis (blocks mediator release)',
    onset: '2–5 min', durationLabel: '~2 hr',
    category: 'endocrine', duration: 3_600_000, transitionMs: 45_000,
    deltas: {},
    scenarioDeltas: {
      // Carcinoid crisis: mediator blockade reverses the vasoplegia and
      // bronchospasm together.
      carcinoidCrisis: {
        HR: -25, NIBP_Sys: +35, NIBP_Dia: +20, ABP_Sys: +35, ABP_Dia: +20,
        SpO2: +5, EtCO2: -10, capnoMorphology: 'normal',
      },
    },
  },

  cyproheptadine: {
    name: 'Cyproheptadine', shortName: 'Cypro',
    description: '5-HT₂A antagonist (PO/NG) — specific antidote for serotonin syndrome',
    onset: '30–60 min (sim: min)', durationLabel: '~6 hr',
    category: 'endocrine', duration: 3_600_000, transitionMs: 90_000,
    deltas: {},
    scenarioDeltas: {
      serotoninSyndrome: { HR: -35, Temp: -1.4, NIBP_Sys: -25, NIBP_Dia: -14, ABP_Sys: -25, ABP_Dia: -14, RR: -6, EtCO2: -6 },
    },
  },

  bromocriptine: {
    name: 'Bromocriptine', shortName: 'Bromo',
    description: 'Dopamine agonist (PO/NG) — restores central dopamine tone in NMS',
    onset: 'Hours (sim: min)', durationLabel: 'Continuous',
    category: 'endocrine', duration: 3_600_000, transitionMs: 120_000,
    deltas: {},
    scenarioDeltas: {
      nms: { HR: -18, Temp: -1.0, NIBP_Sys: -12, NIBP_Dia: -7, ABP_Sys: -12, ABP_Dia: -7, EtCO2: -6 },
    },
  },

  activeCooling: {
    name: 'Active Cooling', shortName: 'Cooling',
    description: 'Cooling blanket + cold IV fluids + ice packs — for hyperthermic crises',
    onset: '5–15 min', durationLabel: 'Continuous',
    category: 'equipment', duration: 3_600_000, transitionMs: 120_000,
    deltas: { Temp: -0.8 },
    scenarioDeltas: {
      serotoninSyndrome: { Temp: -1.5, HR: -8 },
      nms:               { Temp: -1.8, HR: -8 },
      thyroidStorm:      { Temp: -1.4, HR: -6 },
    },
    modifierDeltas: {
      // MH: cooling is adjunctive to dantrolene, not a substitute.
      malignantHyperthermia: { Temp: -1.5, HR: -5 },
    },
  },

  passiveLegRaise: {
    name: 'Passive Leg Raise', shortName: 'PLR',
    description: 'Reversible auto-bolus (~300 mL) — BP rises ONLY if fluid-responsive; effect fades as legs lower',
    onset: '30–60 s', durationLabel: '~2 min (transient)',
    category: 'maneuver', duration: 120_000, transitionMs: 15_000,
    // Euvolemic / vasoplegic patient: essentially no change — that IS the test.
    deltas: { NIBP_Sys: +3, NIBP_Dia: +2, ABP_Sys: +3, ABP_Dia: +2 },
    modifierDeltas: {
      hypovolemia: { NIBP_Sys: +16, NIBP_Dia: +9, ABP_Sys: +16, ABP_Dia: +9, HR: -8 },
    },
    scenarioDeltas: {
      epicHemorrhage:        { NIBP_Sys: +14, NIBP_Dia: +8, ABP_Sys: +14, ABP_Dia: +8, HR: -8 },
      obgynHemorrhagicShock: { NIBP_Sys: +14, NIBP_Dia: +8, ABP_Sys: +14, ABP_Dia: +8, HR: -8 },
      palsHypovolemicShock:  { NIBP_Sys: +12, NIBP_Dia: +7, ABP_Sys: +12, ABP_Dia: +7, HR: -8 },
      dka:                   { NIBP_Sys: +12, NIBP_Dia: +7, ABP_Sys: +12, ABP_Dia: +7, HR: -6 },
      adrenalCrisis:         { NIBP_Sys: +10, NIBP_Dia: +6, ABP_Sys: +10, ABP_Dia: +6, HR: -5 },
    },
  },

  vagalManeuver: {
    name: 'Vagal Maneuver', shortName: 'Vagal',
    description: 'Modified Valsalva / carotid massage — converts ~25–40% of stable SVT without drugs',
    onset: 'Immediate', durationLabel: 'One attempt',
    category: 'maneuver', duration: 300_000, transitionMs: 8_000,
    deltas: { HR: -6 },
    scenarioDeltas: {
      palsSvtStable:       { HR: -95, rhythm: 'normal' },
      rapidSVTHypotension: { HR: -20 },   // unstable SVT: rarely converts — cardiovert instead
    },
  },

  recruitmentManeuver: {
    name: 'Lung Recruitment', shortName: 'Recruit',
    description: 'Sustained inflation (~40 cmH₂O × 30 s) — reopens collapsed alveoli  ↑SpO₂ ↑compliance; transient ↓BP during the hold',
    onset: '1–2 min', durationLabel: '~30 min',
    category: 'maneuver', duration: 1_800_000, transitionMs: 30_000,
    deltas: { SpO2: +2, NIBP_Sys: -6, NIBP_Dia: -3, ABP_Sys: -6, ABP_Dia: -3 },
    scenarioDeltas: {
      palsPneumonia:  { SpO2: +7, NIBP_Sys: -8, NIBP_Dia: -4, ABP_Sys: -8, ABP_Dia: -4 },
      obAspiration:   { SpO2: +6, NIBP_Sys: -8, NIBP_Dia: -4, ABP_Sys: -8, ABP_Dia: -4 },
      obPulmonaryEdema: { SpO2: +5, NIBP_Sys: -8, NIBP_Dia: -4, ABP_Sys: -8, ABP_Dia: -4 },
    },
  },

  proning: {
    name: 'Prone Positioning', shortName: 'Prone',
    description: 'V/Q matching + dorsal recruitment — slow, durable oxygenation improvement in ARDS-pattern hypoxemia',
    onset: '30–60 min (sim: min)', durationLabel: 'Continuous',
    category: 'maneuver', duration: 7_200_000, transitionMs: 120_000,
    deltas: { SpO2: +1 },
    scenarioDeltas: {
      palsPneumonia:    { SpO2: +9, RR: -4 },
      obAspiration:     { SpO2: +7, RR: -3 },
      obPulmonaryEdema: { SpO2: +5, RR: -2 },
    },
  },

  durantsManeuver: {
    name: "Durant's Maneuver", shortName: "Durant's",
    description: 'Left-lateral head-down position — traps venous air in the RV apex, restoring RVOT flow',
    onset: '1–2 min', durationLabel: 'Positioned',
    category: 'maneuver', duration: 1_800_000, transitionMs: 30_000,
    deltas: {},
    scenarioDeltas: {
      airEmbolism: { HR: -14, EtCO2: +8, NIBP_Sys: +14, NIBP_Dia: +8, ABP_Sys: +14, ABP_Dia: +8, SpO2: +4 },
      obVAE:       { HR: -12, EtCO2: +7, NIBP_Sys: +12, NIBP_Dia: +7, ABP_Sys: +12, ABP_Dia: +7, SpO2: +4 },
    },
  },

  leftUterineDisplacement: {
    name: 'Left Uterine Displacement', shortName: 'LUD',
    description: 'Relieves aortocaval compression ≥20 wk — mandatory during maternal arrest CPR',
    onset: 'Immediate', durationLabel: 'Maintained',
    category: 'maneuver', duration: 7_200_000, transitionMs: 15_000,
    deltas: {},
    scenarioDeltas: {
      obgynCardiacArrest:    { EtCO2: +4, ABP_Sys: +6, ABP_Dia: +3 },       // better CPR output
      obgynHemorrhagicShock: { NIBP_Sys: +8, NIBP_Dia: +5, ABP_Sys: +8, ABP_Dia: +5, HR: -5 },
      amnioticFluidEmbolism: { NIBP_Sys: +6, NIBP_Dia: +4, ABP_Sys: +6, ABP_Dia: +4 },
      obgynHighSpinal:       { NIBP_Sys: +8, NIBP_Dia: +5, ABP_Sys: +8, ABP_Dia: +5 },
    },
  },

  headUpPosition: {
    name: 'Head-Up 30°', shortName: 'Head-up',
    description: 'Reverse Trendelenburg — promotes cerebral venous drainage  ↓ICP (adjunct)',
    onset: '2–5 min', durationLabel: 'Positioned',
    category: 'maneuver', duration: 7_200_000, transitionMs: 45_000,
    deltas: { NIBP_Sys: -4, NIBP_Dia: -2, ABP_Sys: -4, ABP_Dia: -2 },
    scenarioDeltas: {
      // Cushing response attenuates as ICP falls
      increasedICP: { HR: +6, NIBP_Sys: -18, NIBP_Dia: -6, ABP_Sys: -18, ABP_Dia: -6, RR: +2 },
    },
  },

  bronchoscopy: {
    name: 'Bronchoscopy / Suction', shortName: 'Bronch',
    description: 'Clears mucus plug / secretions, confirms tube position — resolves an obstructed capnograph',
    onset: '2–5 min', durationLabel: 'Definitive',
    category: 'procedure', duration: 7_200_000, transitionMs: 45_000,
    deltas: { SpO2: +2 },
    scenarioDeltas: {
      palsAsthma:   { SpO2: +6, EtCO2: -10, capnoMorphology: 'normal' },
      obAspiration: { SpO2: +8, EtCO2: -6, capnoMorphology: 'normal' },
    },
  },

  reboa: {
    name: 'REBOA', shortName: 'REBOA',
    description: 'Aortic balloon occlusion (Zone 1/3) — temporises non-compressible torso/pelvic hemorrhage  ↑↑proximal BP',
    onset: '2–5 min', durationLabel: '≤30–60 min (occlusion clock)',
    category: 'procedure', duration: 1_800_000, transitionMs: 30_000,
    deltas: { NIBP_Sys: +15, NIBP_Dia: +8, ABP_Sys: +15, ABP_Dia: +8 },
    scenarioDeltas: {
      epicHemorrhage:        { NIBP_Sys: +32, NIBP_Dia: +16, ABP_Sys: +32, ABP_Dia: +16, HR: -15 },
      epicTrauma:            { NIBP_Sys: +32, NIBP_Dia: +16, ABP_Sys: +32, ABP_Dia: +16, HR: -15 },
      obgynHemorrhagicShock: { NIBP_Sys: +28, NIBP_Dia: +14, ABP_Sys: +28, ABP_Dia: +14, HR: -12 },
    },
  },

  dialysis: {
    name: 'Dialysis / CRRT', shortName: 'CRRT',
    description: 'Definitive K⁺ removal — for refractory hyperkalemia; slower than shifting but removes the load',
    onset: '30–60 min (sim: min)', durationLabel: 'Continuous',
    category: 'procedure', duration: 7_200_000, transitionMs: 120_000,
    deltas: {},
    scenarioDeltas: {
      hyperkalemia:       { rhythm: 'normal', HR: +14, NIBP_Sys: +12, NIBP_Dia: +8, ABP_Sys: +12, ABP_Dia: +8 },
      hyperkalemicArrest: { rhythm: 'normal' },   // narrows the sine wave; still PEA until ACLS
    },
  },

  // ── Bronchodilators ──
  albuterol: {
    name: 'Albuterol / Salbutamol', shortName: 'Albuterol',
    description: 'β₂ bronchodilator — relieves bronchospasm  ↑HR',
    onset: '2–5 min', durationLabel: '~30 min',
    category: 'bronchodilator', duration: 1_800_000, transitionMs: 60_000,
    deltas: { HR: +8 },
    scenarioDeltas: {
      bronchospasm: { vent: { vte: +100, ppeak: -20, pplat: -5 }, HR: +8, SpO2: +5, EtCO2: -16, capnoMorphology: 'normal' },
      epicBronchospasm: { vent: { vte: +100, ppeak: -20, pplat: -5 }, HR: +8, SpO2: +5, EtCO2: -16, capnoMorphology: 'normal' },
      anaphylaxis: { vent: { vte: +120, ppeak: -22, pplat: -6 }, HR: +6, SpO2: +4, EtCO2: -12, capnoMorphology: 'normal' },
      epicAnaphylaxis: { vent: { vte: +120, ppeak: -22, pplat: -6 }, HR: +6, SpO2: +4, EtCO2: -12, capnoMorphology: 'normal' },
      obAspiration:     { HR: +6, SpO2: +3, EtCO2: -8 },
      // Hyperkalaemic arrest: β₂ agonism drives K⁺ intracellularly, narrowing the
      // sine wave to organised complexes — visible on the next rhythm check. Still
      // PEA until ACLS restores output, so rhythm-only (no delta implying ROSC).
      hyperkalemicArrest: { rhythm: 'normal' },
    },
  },

  // ── Obstetric ──
  oxytocin: {
    name: 'Oxytocin', shortName: 'Pitocin',
    description: 'Uterotonic — contracts uterus (stops PPH). Rapid push → ↓BP ↑HR',
    onset: '<1 min', durationLabel: '~20 min',
    category: 'uterotonic', duration: 1_200_000, transitionMs: 30_000,
    deltas: { HR: +10, NIBP_Sys: -10, NIBP_Dia: -6, ABP_Sys: -10, ABP_Dia: -6 }, // rapid-push vasodilation
    scenarioDeltas: {
      obUterineAtony:        { HR: -25, NIBP_Sys: +30, NIBP_Dia: +18, ABP_Sys: +30, ABP_Dia: +18 }, // uterus contracts, bleeding stops
      obgynHemorrhagicShock: { HR: -15, NIBP_Sys: +18, NIBP_Dia: +10, ABP_Sys: +18, ABP_Dia: +10 },
    },
  },

  perimortemHysterotomy: {
    name: 'Perimortem Hysterotomy',
    shortName: 'PMCD',
    description:
      'Resuscitative (perimortem) caesarean — deliver by ~4 min of maternal arrest. ' +
      'Relieves aortocaval compression → improves venous return, CPR effectiveness ' +
      '(↑EtCO₂) and the chance of maternal ROSC.',
    onset: 'Immediate',
    durationLabel: 'Definitive',
    category: 'obstetric',
    duration: 7_200_000,
    transitionMs: 15_000,
    // Relief of aortocaval compression → better preload/CPR quality (↑EtCO₂) and
    // a modest hemodynamic lift; the definitive step in maternal arrest > 20 wk.
    deltas: {
      HR: -5, EtCO2: +5,
      NIBP_Sys: +12, NIBP_Dia: +6, ABP_Sys: +12, ABP_Dia: +6,
    },
  },

  terbutaline: {
    name: 'Terbutaline', shortName: 'Terb',
    description: 'β₂ agonist — tocolytic (uterine relaxation) + bronchodilator  ↑HR',
    onset: '1–3 min', durationLabel: '~30 min',
    category: 'tocolytic', duration: 1_800_000, transitionMs: 45_000,
    deltas: { HR: +14, NIBP_Sys: -6, NIBP_Dia: -4, ABP_Sys: -6, ABP_Dia: -4 },
    scenarioDeltas: {
      obUterineInversion: { HR: +14 }, // relaxes uterus to allow replacement
      obCordProlapse:     { HR: +12 }, // tocolysis to reduce contractions
      bronchospasm: { vent: { vte: +90, ppeak: -18, pplat: -4 }, HR: +12, SpO2: +4, EtCO2: -12, capnoMorphology: 'normal' },
    },
  },

  // ── Hemostasis ──
  tranexamicAcid: {
    name: 'Tranexamic Acid', shortName: 'TXA',
    description: 'Antifibrinolytic — reduces haemorrhage (indirect BP support)',
    onset: '~10 min', durationLabel: '~3 hr',
    category: 'antifibrinolytic', duration: 1_200_000, transitionMs: 90_000,
    deltas: {}, // no direct haemodynamic effect in a non-bleeding patient
    scenarioDeltas: {
      obgynHemorrhagicShock: { HR: -8, NIBP_Sys: +10, NIBP_Dia: +6, ABP_Sys: +10, ABP_Dia: +6 },
      obUterineAtony:        { HR: -6, NIBP_Sys: +8,  NIBP_Dia: +5, ABP_Sys: +8,  ABP_Dia: +5 },
      obPlacentalAbruption:  { HR: -6, NIBP_Sys: +8,  NIBP_Dia: +5, ABP_Sys: +8,  ABP_Dia: +5 },
      obPlacentaAccreta:     { HR: -6, NIBP_Sys: +8,  NIBP_Dia: +5, ABP_Sys: +8,  ABP_Dia: +5 },
      epicHemorrhage:        { HR: -8, NIBP_Sys: +10, NIBP_Dia: +6, ABP_Sys: +10, ABP_Dia: +6 },
      epicTrauma:            { HR: -8, NIBP_Sys: +10, NIBP_Dia: +6, ABP_Sys: +10, ABP_Dia: +6 },
    },
  },

  // ── Reversal agents ──
  naloxone: {
    name: 'Naloxone', shortName: 'Narcan',
    description: 'Opioid reversal — restores respiratory drive & arousal  ↑RR',
    onset: '1–2 min', durationLabel: '30–60 min',
    category: 'reversal', duration: 2_400_000, transitionMs: 20_000,
    deltas: { RR: +10, HR: +8, NIBP_Sys: +8, NIBP_Dia: +4, ABP_Sys: +8, ABP_Dia: +4 },
    scenarioDeltas: {
      epicDelayedEmergence: { RR: +12, HR: +10, EtCO2: -10, NIBP_Sys: +8, NIBP_Dia: +4, ABP_Sys: +8, ABP_Dia: +4 },
    },
  },

  flumazenil: {
    name: 'Flumazenil', shortName: 'Flumaz',
    description: 'Benzodiazepine reversal — restores arousal & respiration',
    onset: '1–2 min', durationLabel: '30–60 min',
    category: 'reversal', duration: 2_400_000, transitionMs: 20_000,
    deltas: { RR: +6, HR: +4 },
    scenarioDeltas: {
      epicDelayedEmergence: { RR: +8, EtCO2: -6, HR: +4 },
    },
  },

  neostigmine: {
    name: 'Neostigmine + Glyco', shortName: 'Neo',
    description: 'Reverses non-depolarising block (with glycopyrrolate) — TOF recovers',
    onset: '3–5 min', durationLabel: '~1 hr',
    category: 'reversal', duration: 3_000_000, transitionMs: 60_000,
    deltas: { HR: -4 }, // net small: neostigmine bradycardia offset by glycopyrrolate
    scenarioDeltas: {
      epicDelayedEmergence: { RR: +6, HR: -2 },
    },
  },

  // ── Other ──
  methyleneBlue: {
    name: 'Methylene Blue', shortName: 'MB',
    description: 'Reverses methemoglobinemia; vasopressor in vasoplegia  ↑BP',
    onset: '1–5 min', durationLabel: '~30 min',
    category: 'other', duration: 1_800_000, transitionMs: 30_000,
    deltas: { NIBP_Sys: +8, NIBP_Dia: +4, ABP_Sys: +8, ABP_Dia: +4 },
    scenarioDeltas: {
      // Serotonin syndrome: methylene blue is a potent MAO-A inhibitor —
      // it can precipitate or dramatically worsen the syndrome.
      serotoninSyndrome: { HR: +18, Temp: +0.6, NIBP_Sys: +15, NIBP_Dia: +8, ABP_Sys: +15, ABP_Dia: +8 },
      methemoglobinemia: { SpO2: +13, HR: -6 },  // 85 → ~98 as MetHb reduced
      epicHypotension:   { NIBP_Sys: +18, NIBP_Dia: +10, ABP_Sys: +18, ABP_Dia: +10, HR: -6 }, // vasoplegia
    },
  },

  antihistamine: {
    name: 'Diphenhydramine + Famotidine', shortName: 'H1/H2',
    description: 'Antihistamines — anaphylaxis adjunct (not first-line)',
    onset: '5–10 min', durationLabel: '~2 hr',
    category: 'other', duration: 1_800_000, transitionMs: 60_000,
    deltas: { HR: -4 },
    scenarioDeltas: {
      anaphylaxis:     { HR: -8, NIBP_Sys: +6, NIBP_Dia: +4, ABP_Sys: +6, ABP_Dia: +4 },
      epicAnaphylaxis: { HR: -8, NIBP_Sys: +6, NIBP_Dia: +4, ABP_Sys: +6, ABP_Dia: +4 },
    },
  },

  furosemide: {
    name: 'Furosemide', shortName: 'Lasix',
    description: 'Loop diuretic — offloads pulmonary oedema  ↓CVP  ↑SpO₂',
    onset: '5–15 min', durationLabel: '~2 hr',
    category: 'diuretic', duration: 1_800_000, transitionMs: 90_000,
    deltas: { NIBP_Sys: -6, NIBP_Dia: -4, ABP_Sys: -6, ABP_Dia: -4, CVP: -3 },
    scenarioDeltas: {
      obPulmonaryEdema:       { SpO2: +7, RR: -8, CVP: -5, EtCO2: +2 },
      obgynPeripartumCM:      { SpO2: +5, RR: -6, CVP: -4 },
      obgynSCAD:              { SpO2: +4, RR: -5, CVP: -3 },
      epicMyocardialIschemia: { SpO2: +3, CVP: -2 },
    },
  },

  inhaledPulmonaryVasodilator: {
    name: 'Inhaled NO / Epoprostenol', shortName: 'iNO',
    description: 'Pulmonary vasodilator — ↓PVR, offloads RV  ↑SpO₂  ↑BP',
    onset: '2–5 min', durationLabel: 'continuous',
    category: 'other', duration: 1_800_000, transitionMs: 60_000,
    deltas: { SpO2: +3 },
    scenarioDeltas: {
      epicRightHeartFailure: { SpO2: +6, NIBP_Sys: +12, NIBP_Dia: +8, ABP_Sys: +12, ABP_Dia: +8, CVP: -3 },
      epicPulmonaryEmbolism: { SpO2: +5, NIBP_Sys: +14, NIBP_Dia: +8, ABP_Sys: +14, ABP_Dia: +8, EtCO2: +4 },
      amnioticFluidEmbolism: { SpO2: +5, NIBP_Sys: +12, NIBP_Dia: +7, ABP_Sys: +12, ABP_Dia: +7, EtCO2: +3, CVP: -2 },
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Analgesics (opioids) — analgesia + dose-dependent respiratory depression
  //   (↓RR, ↑EtCO₂), mild bradycardia and hypotension. Reversed by naloxone.
  // ═══════════════════════════════════════════════════════════════════════════
  fentanyl: {
    name: 'Fentanyl', shortName: 'Fent',
    description: 'Opioid analgesic — ↓pain, ↓HR/BP; ↓RR ↑EtCO₂ (resp depression)',
    onset: '1–2 min', durationLabel: '30–60 min',
    category: 'analgesic', duration: 1_800_000, transitionMs: 30_000,
    // Dose presets — scale multiplies the deltas below (1 = reference dose).
    doses: [{ label: '50 µg', scale: 0.5 }, { label: '100 µg', scale: 1, default: true }, { label: '250 µg', scale: 1.8 }],
    deltas: { HR: -8, NIBP_Sys: -6, NIBP_Dia: -4, ABP_Sys: -6, ABP_Dia: -4, RR: -6, EtCO2: +6 },
    scenarioDeltas: {
      // Serotonin syndrome: fentanyl is SEROTONERGIC — it feeds the toxidrome.
      serotoninSyndrome: { HR: +12, Temp: +0.4, NIBP_Sys: +10, NIBP_Dia: +6, ABP_Sys: +10, ABP_Dia: +6 },
      // Analgesia blunts pain-driven ischaemia (myocardial O₂ demand ↓)
      epicMyocardialIschemia: { HR: -12, NIBP_Sys: -12, NIBP_Dia: -7, ABP_Sys: -12, ABP_Dia: -7 },
    },
  },

  remifentanil: {
    name: 'Remifentanil', shortName: 'Remi',
    description: 'Ultra-short opioid infusion — potent; ↓HR/BP, marked ↓RR ↑EtCO₂',
    onset: '<1 min', durationLabel: '5–10 min',
    category: 'analgesic', duration: 600_000, transitionMs: 15_000,
    deltas: { HR: -12, NIBP_Sys: -12, NIBP_Dia: -7, ABP_Sys: -12, ABP_Dia: -7, RR: -10, EtCO2: +8 },
  },

  hydromorphone: {
    name: 'Hydromorphone', shortName: 'Dilaudid',
    description: 'Opioid analgesic — longer acting; ↓HR/BP, ↓RR ↑EtCO₂',
    onset: '5–10 min', durationLabel: '3–4 hr',
    category: 'analgesic', duration: 2_700_000, transitionMs: 60_000,
    deltas: { HR: -6, NIBP_Sys: -6, NIBP_Dia: -4, ABP_Sys: -6, ABP_Dia: -4, RR: -5, EtCO2: +5 },
  },

  morphine: {
    name: 'Morphine', shortName: 'Morphine',
    description: 'Opioid analgesic — histamine release → more ↓BP; ↓RR ↑EtCO₂',
    onset: '5–10 min', durationLabel: '3–4 hr',
    category: 'analgesic', duration: 2_700_000, transitionMs: 60_000,
    deltas: { HR: -6, NIBP_Sys: -12, NIBP_Dia: -7, ABP_Sys: -12, ABP_Dia: -7, RR: -6, EtCO2: +6 },
    scenarioDeltas: {
      epicMyocardialIschemia: { HR: -10, NIBP_Sys: -14, NIBP_Dia: -8, ABP_Sys: -14, ABP_Dia: -8 },
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Benzodiazepines — sedation/amnesia, mild resp depression + hypotension.
  //   Lower BIS (EEG slowing). Reversed by flumazenil.
  // ═══════════════════════════════════════════════════════════════════════════
  midazolam: {
    name: 'Midazolam', shortName: 'Versed',
    description: 'Benzodiazepine — sedation/amnesia; mild ↓BP ↓RR; ↓BIS',
    onset: '1–3 min', durationLabel: '30–60 min',
    category: 'benzodiazepine', duration: 1_800_000, transitionMs: 30_000,
    // Dose presets — scale multiplies the deltas below (1 = reference dose).
    doses: [{ label: '1 mg', scale: 0.5 }, { label: '2 mg', scale: 1, default: true }, { label: '5 mg', scale: 1.8 }],
    deltas: { HR: -2, NIBP_Sys: -6, NIBP_Dia: -4, ABP_Sys: -6, ABP_Dia: -4, RR: -3, EtCO2: +3 },
    scenarioDeltas: {
      // Hyperthermic toxidromes: benzodiazepines blunt the agitation-driven
      // sympathetic surge in both serotonin syndrome and NMS.
      serotoninSyndrome: { HR: -18, Temp: -0.4, NIBP_Sys: -14, NIBP_Dia: -8, ABP_Sys: -14, ABP_Dia: -8, RR: -4 },
      nms:               { HR: -12, Temp: -0.3, NIBP_Sys: -10, NIBP_Dia: -6, ABP_Sys: -10, ABP_Dia: -6 },
      // First-line to terminate the LAST seizure
      epicLAST: { HR: -12, RR: -3, EtCO2: +3 },
      // Second-line for a refractory eclamptic seizure — stops convulsion so
      // ventilation resumes (SpO₂/RR recover) despite mild sedative depression.
      obSeizure: { HR: -18, SpO2: +10, RR: +6, EtCO2: -8 },
    },
  },

  lorazepam: {
    name: 'Lorazepam', shortName: 'Ativan',
    description: 'Long-acting benzodiazepine — sedation; mild ↓BP ↓RR; ↓BIS',
    onset: '5–10 min', durationLabel: '6–8 hr',
    category: 'benzodiazepine', duration: 3_600_000, transitionMs: 60_000,
    deltas: { HR: -2, NIBP_Sys: -5, NIBP_Dia: -3, ABP_Sys: -5, ABP_Dia: -3, RR: -3, EtCO2: +3 },
    scenarioDeltas: {
      // Benzodiazepines terminate seizures — convulsion stops, ventilation resumes
      obSeizure: { HR: -18, SpO2: +10, RR: +6, EtCO2: -8 },
      epicLAST:  { HR: -12, RR: -3, EtCO2: +3 },
    },
  },
};
